/**
 * Survey Session component for respondents taking surveys
 * Handles one-question-per-screen conversational flow
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, MessageSquare, Mic, Keyboard, AlertCircle } from 'lucide-react';
import Button from '../../components/Button';
import SurveyWelcomeScreen from './SurveyWelcomeScreen';
import { SurveyTermination } from '../../components/survey/SurveyTermination';
import ImageHotspotResponse from '../../components/survey/ImageHotspotResponse';
import InfoScreenDisplay from '../../components/survey/InfoScreenDisplay';
import { getPublicSurvey, startSurveySession, submitResponse, submitFollowupResponse, completeSurveySession, analyzeResponseForFollowup, shareIdToSurveyId, sendSessionHeartbeat } from '../../utils/surveyApi';
import { UI_TEXT, getRandomAnalyzingMessage } from '../../config/ui-text';
import { ASYNC_ANALYSIS_CONFIG, ANALYSIS_TASK_STATES } from '../../config/constants';
import { generateAndPrepareSetsForUser, evaluateMaxDiffDesign } from './maxDiffUtils';

const API_URL = import.meta.env.VITE_API_URL || '';

// Helper to build full URL for media (handles both uploaded files and external URLs)
const getFullMediaUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
};

/** Backward-compat check: true if question should randomize media order. */
function shouldRandomizeMedia(q: any): boolean {
  if (q?.randomize_media_order != null) return q.randomize_media_order;
  return q?.image_position_mode === 'randomized';
}

interface QuestionResponse {
  question_id: string;
  question_type: string;
  response_data: any;
  state: string;
  created_at: string;
}

interface SessionData {
  id: number;
  state: string;
  current_question_index: number;
  survey_id: number;
  responses: QuestionResponse[];
  responses_count: number;
  question_order?: number[] | null;  // Randomized question order indices
}

const SurveySession: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const isPreviewMode = searchParams.get('preview') === 'true';
  const startFromParam = searchParams.get('startFrom');

  const [survey, setSurvey] = useState<any>(null);
  const [session, setSession] = useState<SessionData | null>(null);
  const previewResponsesRef = useRef<QuestionResponse[]>([]);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(!startFromParam);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(startFromParam ? parseInt(startFromParam) || 0 : 0);
  const [currentResponse, setCurrentResponse] = useState<any>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [followupQuestion, setFollowupQuestion] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState<string>('');
  const [originalResponse, setOriginalResponse] = useState<any>(null);
  const [questionStartTime, setQuestionStartTime] = useState<string>('');
  const [currentMaxDiffSetIndex, setCurrentMaxDiffSetIndex] = useState(0);
  
  // Track option media loading for choice questions
  const [optionMediaLoaded, setOptionMediaLoaded] = useState<Record<string, boolean>>({});
  const [totalOptionMedia, setTotalOptionMedia] = useState<number>(0);
  const [loadedOptionMediaCount, setLoadedOptionMediaCount] = useState<number>(0);
  const [showMaxDiffInstructions, setShowMaxDiffInstructions] = useState(false);
  const [currentConjointScenarioIndex, setCurrentConjointScenarioIndex] = useState(0);
  const [maxDiffResponses, setMaxDiffResponses] = useState<Record<number, {most: string, least: string}>>({});
  const [conjointResponses, setConjointResponses] = useState<Record<number, string>>({});
  const [isTerminated, setIsTerminated] = useState(false);
  const [terminationReason, setTerminationReason] = useState<string | null>(null);
  const [terminationRedirectUrl, setTerminationRedirectUrl] = useState<string | null>(null);
  const [pendingSkipTarget, setPendingSkipTarget] = useState<number | null>(null);
  const [isSkippingConditional, setIsSkippingConditional] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [audioLevels, setAudioLevels] = useState<number[]>(new Array(7).fill(0));

  // Memoize analyzing message to prevent flickering when component re-renders during analysis
  const analyzingMessage = useMemo(() => {
    return isAnalyzing ? getRandomAnalyzingMessage() : '';
  }, [isAnalyzing]);

  // Function to analyze audio and update visual levels
  const analyzeAudio = useCallback(() => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    analyser.getByteFrequencyData(dataArray);

    // Ultra conservative audio analysis - very hard to reach max
    const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
    const normalizedLevel = Math.min(1, average / 50); // Very high threshold

    // Create dynamic levels for each bar
    const newLevels = Array.from({ length: 7 }, (_, i) => {
      const centerIndex = 3;
      const distanceFromCenter = Math.abs(i - centerIndex);

      // Base level decreases from center
      const baseMultiplier = 1 - (distanceFromCenter * 0.15);

      // Add some randomness for natural variation
      const randomVariation = (Math.random() - 0.5) * 0.1;

      // Ultra conservative scaling - extremely hard to reach max
      const scaledLevel = normalizedLevel * 1.5 + 0.5; // Minimal amplification with high baseline
      const level = Math.max(0.5, Math.min(1, scaledLevel * baseMultiplier + randomVariation));

      return level;
    });

    setAudioLevels(newLevels);

    // Continue animation loop
    animationFrameRef.current = requestAnimationFrame(analyzeAudio);
  }, []);

  // Clean up audio analysis on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Function to randomize choices while excluding the "Other" option if it exists
  const randomizeChoices = useCallback((choices: string[], hasOtherOption: boolean = false): string[] => {
    if (!choices || choices.length <= 1) return choices;

    // If there's an "Other" option and it should be excluded from randomization
    const choicesToRandomize = hasOtherOption ? choices.slice(0, -1) : choices;
    const otherOption = hasOtherOption && choices.length > 0 ? choices[choices.length - 1] : null;

    // Fisher-Yates shuffle algorithm for randomization
    const randomized = [...choicesToRandomize];
    for (let i = randomized.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [randomized[i], randomized[j]] = [randomized[j], randomized[i]];
    }

    // Add back the "Other" option at the end if it exists
    return otherOption ? [...randomized, otherOption] : randomized;
  }, []);

  // Function to calculate MaxDiff scores and get top and bottom claims
  const calculateMaxDiffRankedClaims = useCallback((
    responses: Record<number, {most?: string, least?: string, most_appealing?: string, least_appealing?: string}>,
    count: number = 3
  ): { topClaims: string[], bottomClaims: string[] } => {
    // Count most and least selections
    const mostCounts: Record<string, number> = {};
    const leastCounts: Record<string, number> = {};

    // Iterate through all set responses
    Object.values(responses).forEach(response => {
      // Handle both 'most'/'least' and 'most_appealing'/'least_appealing' keys
      const most = response.most_appealing || response.most;
      const least = response.least_appealing || response.least;

      if (most) {
        mostCounts[most] = (mostCounts[most] || 0) + 1;
      }
      if (least) {
        leastCounts[least] = (leastCounts[least] || 0) + 1;
      }
    });

    // Calculate net scores (most = +1, least = -1)
    const allItems = new Set([...Object.keys(mostCounts), ...Object.keys(leastCounts)]);
    const netScores: Record<string, number> = {};

    allItems.forEach(item => {
      const mostScore = (mostCounts[item] || 0) * 1;  // +1 per "most" selection
      const leastScore = (leastCounts[item] || 0) * -1;  // -1 per "least" selection
      netScores[item] = mostScore + leastScore;
    });

    // Sort by score (descending), then alphabetically for ties
    const sortedItems = Object.entries(netScores)
      .sort(([itemA, scoreA], [itemB, scoreB]) => {
        if (scoreB !== scoreA) return scoreB - scoreA;  // Higher score first
        return itemA.localeCompare(itemB);  // Alphabetical for ties
      });

    // Top N = highest scores, Bottom N = lowest scores
    const topClaims = sortedItems.slice(0, count).map(([item]) => item);
    const bottomClaims = sortedItems.slice(-count).reverse().map(([item]) => item);

    return { topClaims, bottomClaims };
  }, []);

  // Get the actual question index using question_order if available
  // This maps display position (currentQuestionIndex) to actual question index in survey.questions
  const getActualQuestionIndex = useCallback((displayIndex: number): number => {
    if (session?.question_order && session.question_order.length > displayIndex) {
      return session.question_order[displayIndex];
    }
    return displayIndex;
  }, [session?.question_order]);

  // Memoized current question that respects question_order
  const currentQuestion = useMemo(() => {
    if (!survey || !survey.questions || currentQuestionIndex >= survey.questions.length) {
      return null;
    }
    const actualIndex = getActualQuestionIndex(currentQuestionIndex);
    return survey.questions[actualIndex];
  }, [survey, currentQuestionIndex, getActualQuestionIndex]);

  // Memoized randomized choices for the current question to prevent re-shuffling on re-renders
  const randomizedChoices = useMemo(() => {
    if (!currentQuestion) {
      return [];
    }

    const originalChoices = currentQuestion.choices || [];
    const shouldRandomize = currentQuestion.randomize_choices;
    const hasOther = currentQuestion.allow_other;

    if (!shouldRandomize || originalChoices.length <= 1) {
      return originalChoices;
    }

    return randomizeChoices(originalChoices, hasOther);
  }, [currentQuestion, randomizeChoices]);

  /** Question-level media_urls in display order; originalIndices[i] is the storage index for slot i (for choice-image mode). */
  const questionMediaDisplay = useMemo(() => {
    if (!currentQuestion?.media_urls?.length) {
      return { orderedUrls: [] as string[], originalIndices: [] as number[] };
    }
    const urls = currentQuestion.media_urls;
    const n = urls.length;
    if (n <= 1) {
      return { orderedUrls: [...urls], originalIndices: n === 1 ? [0] : [] };
    }
    const identity = Array.from({ length: n }, (_, i) => i);
    if (shouldRandomizeMedia(currentQuestion)) {
      const pairs: { url: string; orig: number }[] = urls.map((url: string, orig: number) => ({ url, orig }));
      for (let i = n - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
      }
      return {
        orderedUrls: pairs.map((p) => p.url),
        originalIndices: pairs.map((p) => p.orig),
      };
    }
    return { orderedUrls: [...urls], originalIndices: identity };
  }, [
    currentQuestion?.id,
    JSON.stringify(currentQuestion?.media_urls),
    currentQuestion?.randomize_media_order,
    currentQuestion?.image_position_mode,
  ]);

  const orderedQuestionMediaUrls = questionMediaDisplay.orderedUrls;
  const questionMediaOriginalIndices = questionMediaDisplay.originalIndices;

  // Memoized randomized matrix rows for matrix questions
  const randomizedMatrixRows = useMemo(() => {
    if (!currentQuestion || currentQuestion.type !== 'matrix_rating') {
      return [];
    }

    const originalRows = currentQuestion.matrix_rows || currentQuestion.choices || [];
    const shouldRandomize = currentQuestion.randomize_choices;

    if (!shouldRandomize || originalRows.length <= 1) {
      return originalRows;
    }

    // For matrix questions, randomize rows without excluding any items (no "Other" option)
    return randomizeChoices(originalRows, false);
  }, [currentQuestion, randomizeChoices]);

  // Memoized MaxDiff sets for the current question
  const maxDiffSets = useMemo(() => {
    if (!currentQuestion || currentQuestion.type !== 'max_diff' || !currentQuestion.choices || !session) {
      return [];
    }

    // Use session ID as unique identifier for reproducible randomization per user
    const sets = generateAndPrepareSetsForUser(currentQuestion.choices, session.id.toString(), 4, 4);

    // Evaluate and log the design quality for this session
    const evaluation = evaluateMaxDiffDesign(sets, currentQuestion.choices);
    console.log(`[MaxDiff Evaluation - Session ${session.id}]`, evaluation);

    return sets;
  }, [currentQuestion, session]);

  // Reset state when question changes
  useEffect(() => {
    setCurrentMaxDiffSetIndex(0);
    setCurrentConjointScenarioIndex(0);
    setMaxDiffResponses({});
    setConjointResponses({});
    setCurrentResponse('');
    setFollowupQuestion(null);
    setQuestionStartTime(new Date().toISOString());
    
    // Reset option media loading state
    setOptionMediaLoaded({});
    setLoadedOptionMediaCount(0);
    
    // Calculate total option media for current question
    if (currentQuestion?.option_media) {
      const optionMediaValues = Object.values(currentQuestion.option_media) as string[][];
      const total = optionMediaValues.reduce(
        (sum, urls) => sum + (urls?.length || 0), 
        0
      );
      setTotalOptionMedia(total);
    } else {
      setTotalOptionMedia(0);
    }
  }, [currentQuestionIndex, currentQuestion]);

  // Helper function to evaluate percentage allocation conditions
  const evaluatePercentageCondition = useCallback((responseData: any[], condition: any): boolean => {
    // Extract the dict value from original_response
    const originalResponse = responseData?.find((item: any) => item.type === 'original_response');
    if (!originalResponse?.value || typeof originalResponse.value !== 'object') return true;

    const allocations = originalResponse.value as Record<string, number>;
    const optionValue = allocations[condition.option_key];

    if (optionValue === undefined) return true; // Option not found, show by default

    const threshold = condition.threshold ?? 0;

    switch (condition.operator) {
      case '>':  return optionValue > threshold;
      case '>=': return optionValue >= threshold;
      case '=':  return optionValue === threshold;
      case '<':  return optionValue < threshold;
      case '<=': return optionValue <= threshold;
      default:   return true;
    }
  }, []);

  // Helper function to evaluate choice-based conditions
  const evaluateChoiceCondition = useCallback((responseData: any[], condition: any): boolean => {
    const extractSelectedValues = (data: any[]): string[] => {
      if (!data || !Array.isArray(data)) return [];
      const values: string[] = [];
      for (const item of data) {
        if (item.type === 'single_choice' || item.type === 'multiple_choice') {
          values.push(String(item.value));
        } else if (item.type === 'original_response') {
          values.push(String(item.value));
        }
      }
      return values;
    };

    const selectedValues = extractSelectedValues(responseData);

    if (condition.operator === 'selected') {
      return selectedValues.includes(condition.value);
    } else if (condition.operator === 'not_selected') {
      return !selectedValues.includes(condition.value);
    }
    return true;
  }, []);

  const evaluateSingleCondition = useCallback((condition: any, responses: any[]): boolean => {
    const sourceResponse = responses?.find(
      (r: any) => r.question_id === condition.source_question_id
    );
    if (!sourceResponse?.response_data) return true;

    const isPercentageCondition = ['>', '>=', '=', '<', '<='].includes(condition.operator);
    if (isPercentageCondition) {
      return evaluatePercentageCondition(sourceResponse.response_data, condition);
    } else {
      return evaluateChoiceCondition(sourceResponse.response_data, condition);
    }
  }, [evaluatePercentageCondition, evaluateChoiceCondition]);

  // Evaluate display conditions -- supports compound AND/OR conditions
  const evaluateDisplayCondition = useCallback((question: any, responses: any[]): boolean => {
    if (!question?.display_condition) return true;

    const dc = question.display_condition;

    // New compound format: { logic, conditions[] }
    if (dc.conditions && Array.isArray(dc.conditions) && dc.conditions.length > 0) {
      const logic = dc.logic || 'and';
      if (logic === 'or') {
        return dc.conditions.some((c: any) => evaluateSingleCondition(c, responses));
      }
      return dc.conditions.every((c: any) => evaluateSingleCondition(c, responses));
    }

    // Legacy flat format: { source_question_id, operator, value, ... }
    if (dc.source_question_id) {
      return evaluateSingleCondition(dc, responses);
    }

    return true;
  }, [evaluateSingleCondition]);

  // Check if current question should be skipped (memoized to prevent flashing)
  const shouldSkipCurrentQuestion = useMemo(() => {
    if (!session || !survey || !currentQuestion || showWelcomeScreen) return false;
    if (!currentQuestion.display_condition) return false;
    return !evaluateDisplayCondition(currentQuestion, session.responses || []);
  }, [session, survey, currentQuestion, showWelcomeScreen, evaluateDisplayCondition]);

  // Evaluate display conditions and skip questions that don't meet conditions
  useEffect(() => {
    const skipConditionalQuestion = async () => {
      if (!shouldSkipCurrentQuestion || isSubmitting || isSkippingConditional) return;

      setIsSkippingConditional(true);

      // Preview mode: just skip locally without API calls
      if (isPreviewMode) {
        if (currentQuestionIndex + 1 < survey!.questions.length) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          setIsTerminated(true);
          setTerminationReason('completed');
        }
        setIsSkippingConditional(false);
        return;
      }

      try {
        const result = await submitResponse(
          session!.id,
          currentQuestion.id,
          { skipped_reason: 'conditionally_skipped' },
          currentQuestion.type,
          questionStartTime,
          true,
          null
        );

        setSession(result.session_data);

        if (currentQuestionIndex + 1 < survey!.questions.length) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          const completeResult = await completeSurveySession(session!.id);
          if (completeResult.redirect_url) {
            window.location.href = completeResult.redirect_url;
          } else {
            navigate(`/survey-complete/${session!.id}`);
          }
        }
      } catch (error) {
        console.error('Failed to skip conditional question:', error);
      } finally {
        setIsSkippingConditional(false);
      }
    };

    skipConditionalQuestion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSkipCurrentQuestion, currentQuestionIndex, isSubmitting, isSkippingConditional]);

  // Load survey and create session
  useEffect(() => {
    const loadSurveyAndCreateSession = async () => {
      if (!shareId || session) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        const surveyId = shareIdToSurveyId(shareId);

        // Preview mode: load survey via authenticated endpoint, no session creation
        if (isPreviewMode) {
          const { getSurvey } = await import('../../utils/surveyApi');
          const surveyResult = await getSurvey(surveyId);
          setSurvey(surveyResult.survey);
          // Create a fake local session for preview
          setSession({
            id: -1,
            state: 'in_progress',
            current_question_index: startFromParam ? parseInt(startFromParam) || 0 : 0,
            survey_id: surveyId,
            responses: [],
            responses_count: 0,
            question_order: null,
          });
          setIsLoading(false);
          return;
        }

        const surveyResult = await getPublicSurvey(surveyId);
        setSurvey(surveyResult.survey);

        // Extract tracking ID from URL query parameters using survey's configured tracking param names
        let trackingId: string | null = null;

        console.log('Survey redirect_config:', surveyResult.survey.redirect_config);
        console.log('URL search params:', Object.fromEntries(searchParams.entries()));

        if (surveyResult.survey.redirect_config?.tracking_param) {
          // Get the tracking parameter names from the survey's redirect config
          const trackingParamNames = Object.keys(surveyResult.survey.redirect_config.tracking_param);
          console.log('Configured tracking parameter names:', trackingParamNames);

          // Look for any of these parameter names in the URL
          for (const paramName of trackingParamNames) {
            const paramValue = searchParams.get(paramName);
            console.log(`Checking for param "${paramName}":`, paramValue);
            if (paramValue) {
              trackingId = paramValue;
              console.log(`✓ Found tracking ID "${trackingId}" from configured parameter "${paramName}"`);
              break;
            }
          }

          if (!trackingId) {
            console.warn('No tracking ID found in URL parameters');
          }
        } else {
          console.log('No redirect_config or tracking_param configured for this survey');
        }

        // Start or resume session for this survey with tracking ID
        const sessionResult = await startSurveySession(surveyId, trackingId || undefined);

        // Validate session result exists
        if (!sessionResult) {
          console.error('No response from session API');
          setError('Failed to start survey. Please try again.');
          setIsLoading(false);
          return;
        }

        // Handle different response statuses
        if (sessionResult.status === 'quota_full') {
          console.log('Survey quota is full');
          setIsTerminated(true);
          setTerminationReason('quota_full');
          setTerminationRedirectUrl(sessionResult.redirect_url || null);
          setIsLoading(false);
          return;
        }

        if (sessionResult.status === 'already_completed') {
          console.log('Survey already completed for this tracking ID');
          setIsTerminated(true);
          setTerminationReason('completed');
          setTerminationRedirectUrl(sessionResult.redirect_url || null);
          setIsLoading(false);
          return;
        }

        if (sessionResult.status === 'screened_out') {
          console.log('Session was previously screened out');
          setIsTerminated(true);
          setTerminationReason('screened_out');
          setTerminationRedirectUrl(sessionResult.redirect_url || null);
          setIsLoading(false);
          return;
        }

        // Validate session data exists
        if (!sessionResult.session_data) {
          console.error('No session data returned from API');
          setError('Failed to start survey. Please try again.');
          setIsLoading(false);
          return;
        }

        // For resumed sessions, restore question index and skip welcome screen
        if (sessionResult.status === 'resumed') {
          console.log(`Resuming session ${sessionResult.session_data.id} at question ${sessionResult.session_data.current_question_index}`);
          setSession(sessionResult.session_data);

          // Restore question index if session has progressed
          if (sessionResult.session_data.current_question_index > 0) {
            setCurrentQuestionIndex(sessionResult.session_data.current_question_index);
            // Skip welcome screen for resumed sessions
            setShowWelcomeScreen(false);
          }
          return;
        }

        // New session created
        setSession(sessionResult.session_data);

      } catch (error) {
        console.error('Failed to load survey or create session:', error);
        setError('Failed to start survey. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSurveyAndCreateSession();
  }, [shareId, searchParams]);

  // Session heartbeat: ping every 60s to keep the session marked as active
  useEffect(() => {
    if (!session?.id || isPreviewMode) return;
    const intervalId = setInterval(() => {
      sendSessionHeartbeat(session.id);
    }, 60_000);

    const handleBeforeUnload = () => {
      sendSessionHeartbeat(session.id);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [session?.id]);

  // Track question start time when question changes
  useEffect(() => {
    if (survey && session && !followupQuestion) {
      setQuestionStartTime(new Date().toISOString());
    }
  }, [currentQuestionIndex, survey, session, followupQuestion]);

  const handleResponseChange = useCallback((value: any) => {
    setCurrentResponse(value);
  }, []);

  const handleStartSurvey = useCallback(() => {
    setShowWelcomeScreen(false);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      // Request microphone access with enhanced audio settings for better quality
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,  // Reduce echo feedback
          noiseSuppression: true,  // Filter background noise
          sampleRate: 44100        // High quality sample rate
        }
      });

      // Check for OpenAI-supported audio formats in order of preference
      // Note: OpenAI supports .mp3, .mp4, .mpeg, .mpga, .m4a, .wav, .webm
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'              // WebM with Opus (OpenAI supported)
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'               // MP4 (OpenAI supported, Safari preferred)
        : MediaRecorder.isTypeSupported('audio/wav')
        ? 'audio/wav'               // WAV (OpenAI supported, universal)
        : '';

      if (!mimeType) {
        throw new Error('No supported audio format found');
      }

      // Set up Web Audio API for real-time audio analysis
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();

      // Configure analyser for responsive visualization
      analyser.fftSize = 512; // Higher resolution for better accuracy
      analyser.smoothingTimeConstant = 0.1; // Much less smoothing for immediate response
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;

      source.connect(analyser);

      // Store references for cleanup and animation
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Start audio level analysis animation
      animationFrameRef.current = requestAnimationFrame(analyzeAudio);

      // Create MediaRecorder with optimal settings
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000 // 128 kbps for good quality without huge files
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Handle audio data as it becomes available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log(`Audio chunk received: ${event.data.size} bytes`);
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording completion
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        console.log(`Final audio blob: ${audioBlob.size} bytes, MIME type: ${mimeType}`);

        // Validate minimum audio data size (1KB minimum for meaningful transcription)
        if (audioBlob.size < 1000) {
          console.warn(`Audio recording too short: ${audioBlob.size} bytes`);
          setError('Recording too short. Please speak for at least 2 seconds.');
          // Clean up media stream
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        // Proceed with transcription for valid audio, passing the actual MIME type
        await transcribeAudio(audioBlob, mimeType);
        // Always clean up the media stream to prevent hanging
        stream.getTracks().forEach(track => track.stop());
      };

      // Handle recording errors
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setError('Recording failed. Please try again.');
        setIsRecording(false);
        // Clean up on error
        stream.getTracks().forEach(track => track.stop());
      };

      // Start recording with data collection every 1 second to prevent data loss
      mediaRecorder.start(1000);

      // Verify recording actually started before updating UI state
      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          setIsRecording(true);
          console.log('Recording started successfully');
        } else {
          throw new Error('Failed to start recording');
        }
      }, 100);

    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Could not access microphone. Please check your permissions.');
      setIsRecording(false);
    }
  }, []);

  const stopRecording = useCallback(() => {
    // Check if MediaRecorder exists and is currently recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log('Stopping recording...');
      // Stop the recording - this will trigger the onstop event
      mediaRecorderRef.current.stop();
      // Update UI state immediately to show recording has stopped
      setIsRecording(false);
    } else {
      console.warn('Cannot stop recording: MediaRecorder not recording');
    }

    // Stop audio analysis animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Clean up audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
    audioContextRef.current = null;
    analyserRef.current = null;

    // Reset audio levels to zero
    setAudioLevels(new Array(7).fill(0));
  }, []);

  const transcribeAudio = useCallback(async (audioBlob: Blob, mimeType: string) => {
    try {
      console.log(`Starting transcription for audio blob: ${audioBlob.size} bytes, MIME type: ${mimeType}`);
      setIsTranscribing(true);

      // Determine file extension based on MIME type (OpenAI supported formats only)
      const getFileExtension = (mimeType: string): string => {
        if (mimeType.includes('webm')) return '.webm';
        if (mimeType.includes('mp4')) return '.mp4';
        if (mimeType.includes('wav')) return '.wav';
        if (mimeType.includes('mp3')) return '.mp3';
        if (mimeType.includes('m4a')) return '.m4a';
        return '.webm'; // Default fallback to WebM (OpenAI supported)
      };

      const fileExtension = getFileExtension(mimeType);
      const fileName = `recording${fileExtension}`;

      // Create form data with audio file for backend transcription
      const formData = new FormData();
      formData.append('audio', audioBlob, fileName);

      // Send audio to backend transcription endpoint
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Transcription request failed:', errorText);
        throw new Error(`Transcription failed: ${response.status}`);
      }

      const result = await response.json();
      console.log('Transcription response:', result);

      // Check if transcription was started successfully
      if (result.status === 'processing' && result.task_id) {
        console.log(`Polling for transcription result with task ID: ${result.task_id}`);
        // Poll for transcription result using the task ID
        const transcribedText = await pollTranscriptionResult(result.task_id);

        console.log(`Transcription completed: "${transcribedText}"`);
        // Append transcribed text to existing response, or set as new response
        setCurrentResponse((prevResponse: any) => {
          const newResponse = prevResponse ? `${prevResponse} ${transcribedText}` : transcribedText;
          console.log(`Updated response: "${newResponse}"`);
          return newResponse;
        });
      } else {
        throw new Error(result.error || 'Transcription failed - no task ID returned');
      }

    } catch (error) {
      console.error('Transcription error:', error);
      // Show user-friendly error message and suggest text input fallback
      setError('Failed to transcribe audio. Please try typing your response.');
    } finally {
      // Always reset transcribing state
      setIsTranscribing(false);
    }
  }, [currentResponse]);

  const pollTranscriptionResult = useCallback(async (taskId: string): Promise<string> => {
    const maxAttempts = 30; // 30 seconds timeout
    const pollInterval = 1000; // 1 second intervals

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/transcribe/status/${taskId}`);

        if (!response.ok) {
          throw new Error('Failed to check transcription status');
        }

        const result = await response.json();

        if (result.status === 'completed') {
          return result.text || '';
        } else if (result.status === 'failed') {
          throw new Error(result.error || 'Transcription failed');
        }

        // Still processing, wait before next poll
        await new Promise(resolve => setTimeout(resolve, pollInterval));

      } catch (error) {
        console.error('Error polling transcription status:', error);
        throw error;
      }
    }

    throw new Error('Transcription timeout');
  }, []);

  const handlePreviewSkip = useCallback(() => {
    if (!survey?.questions) return;
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex >= survey.questions.length) {
      setIsTerminated(true);
      setTerminationReason('completed');
    } else {
      setCurrentResponse('');
      setFollowupQuestion(null);
      setCurrentQuestionIndex(nextIndex);
    }
  }, [currentQuestionIndex, survey]);

  const handleSubmitResponse = useCallback(async (responseValue?: any) => {
    if (!session || !survey || !survey.questions || currentQuestionIndex >= survey.questions.length) return;

    const actualQuestionIndex = session.question_order && session.question_order.length > currentQuestionIndex
      ? session.question_order[currentQuestionIndex]
      : currentQuestionIndex;
    const questionToSubmit = survey.questions[actualQuestionIndex];
    const responseToSubmit = responseValue !== undefined ? responseValue : currentResponse;

    // Preview mode: store response locally, skip all API calls and AI followup
    if (isPreviewMode) {
      const newResponse: QuestionResponse = {
        question_id: questionToSubmit.id,
        question_type: questionToSubmit.type,
        response_data: [{ type: 'original_response', value: responseToSubmit }],
        state: 'answered',
        created_at: new Date().toISOString(),
      };
      previewResponsesRef.current = [...previewResponsesRef.current.filter(r => r.question_id !== questionToSubmit.id), newResponse];
      setSession(prev => prev ? { ...prev, responses: [...prev.responses.filter(r => r.question_id !== questionToSubmit.id), newResponse] } : prev);

      const nextIndex = currentQuestionIndex + 1;
      if (nextIndex >= survey.questions.length) {
        setIsTerminated(true);
        setTerminationReason('completed');
      } else {
        setCurrentResponse('');
        setFollowupQuestion(null);
        setCurrentQuestionIndex(nextIndex);
      }
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      let result;
      let shouldAdvanceQuestion = true; // Default to advancing question

      if (followupQuestion) {
        // This is a follow-up response
        result = await submitFollowupResponse(
          session.id,
          questionToSubmit.id,
          followupQuestion,
          responseToSubmit
        );
      } else {
        // Determine if this should advance to next question
        let actualResponseData = responseToSubmit;

        // Handle MaxDiff progression logic
        if (questionToSubmit.type === 'max_diff' && session) {
          const sets = generateAndPrepareSetsForUser(questionToSubmit.choices || [], session.id.toString(), 4, 4);

          // Evaluate and log the design quality
          const evaluation = evaluateMaxDiffDesign(sets, questionToSubmit.choices || []);
          console.log(`[MaxDiff Evaluation - Session ${session.id} - Submit Response]`, evaluation);

          const currentSetIndex = currentMaxDiffSetIndex;

          // Store the response for this set
          const newMaxDiffResponses = { ...maxDiffResponses };
          newMaxDiffResponses[currentSetIndex] = responseToSubmit;
          setMaxDiffResponses(newMaxDiffResponses);

          // Format response data for backend with set index
          actualResponseData = {
            most: responseToSubmit.most_appealing || responseToSubmit.most,
            least: responseToSubmit.least_appealing || responseToSubmit.least,
            set_index: currentSetIndex
          };

          // Check if there are more sets to show
          if (currentSetIndex < sets.length - 1) {
            shouldAdvanceQuestion = false;
            // Move to next set after response is submitted
            setTimeout(() => {
              setCurrentMaxDiffSetIndex(currentSetIndex + 1);
              setCurrentResponse(''); // Clear response for next set
            }, 100);
          } else {
            // This was the last set, reset for next question
            setCurrentMaxDiffSetIndex(0);
            setMaxDiffResponses({});
          }
        }

        // Handle Conjoint Analysis progression logic
        if (questionToSubmit.type === 'conjoint_analysis' && questionToSubmit.conjoint_attributes) {
          const currentScenarioIndex = currentConjointScenarioIndex;

          // Store the response for this scenario
          const newConjointResponses = { ...conjointResponses };
          newConjointResponses[currentScenarioIndex] = responseToSubmit;
          setConjointResponses(newConjointResponses);

          // Find the selected profile details
          const attributes = questionToSubmit.conjoint_attributes || [];
          const allProfiles = (() => {
            // Generate the same profiles as shown in the UI
            const generateOrthogonalDesign = () => {
              const totalLevels = attributes.reduce((sum, attr) => sum + attr.levels.length, 0);
              const minProfilesNeeded = Math.max(
                totalLevels,
                attributes.length * 2,
                12
              );

              const profiles = [];
              for (let i = 0; i < minProfilesNeeded; i++) {
                const profile = {};
                attributes.forEach((attr, attrIndex) => {
                  const rotationMultiplier = attrIndex + 1;
                  const levelIndex = Math.floor(i / rotationMultiplier) % attr.levels.length;
                  profile[attr.name] = attr.levels[levelIndex];
                });

                profiles.push({
                  id: `profile_${i + 1}`,
                  attributes: profile
                });
              }
              return profiles;
            };
            return generateOrthogonalDesign();
          })();

          // Find the selected profile
          const selectedProfile = allProfiles.find(p => p.id === responseToSubmit);

          // Format response data for backend with scenario index and profile details
          actualResponseData = {
            choice: responseToSubmit,
            profile_details: selectedProfile ? selectedProfile.attributes : null,
            scenario_index: currentScenarioIndex
          };

          // Calculate optimal number of scenarios based on conjoint design principles
          // Standard practice: minimum 8-12 choice tasks for reliable utility estimation
          // Can be made configurable via question properties in the future
          const totalScenarios = questionToSubmit.conjoint_scenarios || Math.min(12, Math.max(8, attributes.length * 2));
          if (currentScenarioIndex < totalScenarios - 1) {
            shouldAdvanceQuestion = false;
            // Move to next scenario after response is submitted
            setTimeout(() => {
              setCurrentConjointScenarioIndex(currentScenarioIndex + 1);
              setCurrentResponse(''); // Clear response for next scenario
            }, 100);
          } else {
            // This was the last scenario, reset for next question
            setCurrentConjointScenarioIndex(0);
            setConjointResponses({});
          }
        }

        // Determine input mode based on current interface state
        let inputMode = null; // Default to null

        if (showTextInput) {
          inputMode = 'text';
        } else if (!showTextInput && (questionToSubmit.type === 'short_text' || questionToSubmit.type === 'long_text')) {
          // Only set to 'mic' for text input questions where voice is actually available
          inputMode = 'mic';
        }

        // This is an original response
        result = await submitResponse(
          session.id,
          questionToSubmit.id,
          actualResponseData,
          questionToSubmit.type,
          questionStartTime,
          shouldAdvanceQuestion,
          inputMode
        );
      }

      setSession(result.session_data);

      // Check if session was terminated
      if (result.status === 'terminated') {
        setIsTerminated(true);
        setTerminationReason(result.termination_reason || null);
        setTerminationRedirectUrl(result.redirect_url || null);
        setIsSubmitting(false);
        return; // Stop processing, show termination screen
      }

      // Check if skip logic was applied - defer until after follow-ups complete
      let skipTarget: number | null = pendingSkipTarget; // From previous follow-up cycle
      if (result.skip_applied && result.next_question_index !== undefined) {
        console.log(`Skip logic detected: will jump to question index ${result.next_question_index} after follow-ups`);
        skipTarget = result.next_question_index;
        setPendingSkipTarget(result.next_question_index);
      }

      // Check if AI follow-up is enabled for this question
      // For MaxDiff questions, only trigger follow-up after the LAST set is completed
      const shouldTriggerFollowup = questionToSubmit.enable_ai_followup &&
        (followupQuestion || // Already in follow-up conversation, continue analyzing
         questionToSubmit.type !== 'max_diff' ||
         (questionToSubmit.type === 'max_diff' && currentMaxDiffSetIndex === maxDiffSets.length - 1));

      if (shouldTriggerFollowup) {
        setIsAnalyzing(true);
        setAnalysisProgress('Starting analysis...');

        try {
          // Analyze response for potential follow-up with progress tracking
          // Use responseToSubmit to ensure we get the actual user's choice/response
          let userResponseForAnalysis = typeof responseToSubmit === 'object'
            ? JSON.stringify(responseToSubmit)
            : String(responseToSubmit);

          // For MaxDiff questions, calculate top and bottom claims and format as context
          let followupContext: string | undefined = undefined;
          if (questionToSubmit.type === 'max_diff') {
            // Use the complete maxDiffResponses state which has all sets
            const completedResponses = { ...maxDiffResponses };
            completedResponses[currentMaxDiffSetIndex] = responseToSubmit;

            // Override userResponseForAnalysis to include ALL MaxDiff responses
            userResponseForAnalysis = JSON.stringify(completedResponses);

            const { topClaims, bottomClaims } = calculateMaxDiffRankedClaims(completedResponses, 3);
            console.log('MaxDiff claims calculated:', { topClaims, bottomClaims });

            // Format as context string with both top and bottom claims
            const contextParts: string[] = [];
            if (topClaims.length > 0) {
              contextParts.push(`Top ${topClaims.length} MOST appealing options:\n${topClaims.map((claim, idx) => `${idx + 1}. ${claim}`).join('\n')}`);
            }
            if (bottomClaims.length > 0) {
              contextParts.push(`Top ${bottomClaims.length} LEAST appealing options:\n${bottomClaims.map((claim, idx) => `${idx + 1}. ${claim}`).join('\n')}`);
            }
            if (contextParts.length > 0) {
              followupContext = contextParts.join('\n\n');
            }
          }

          const analysis = await analyzeResponseForFollowup(
            session.id,
            questionToSubmit.id,
            questionToSubmit.text,
            userResponseForAnalysis,
            questionToSubmit.type,
            {
              timeout: ASYNC_ANALYSIS_CONFIG.POLL_TIMEOUT_MS,
              pollInterval: ASYNC_ANALYSIS_CONFIG.POLL_INTERVAL_MS,
              context: followupContext,
              onProgress: (status: string, message: string) => {
                console.log(`Analysis progress: ${status} - ${message}`);
                // Update progress message based on status
                switch (status) {
                  case ANALYSIS_TASK_STATES.PENDING:
                    setAnalysisProgress('Analysis task queued...');
                    break;
                  case ANALYSIS_TASK_STATES.PROCESSING:
                    setAnalysisProgress('AI is analyzing your response...');
                    break;
                  default:
                    setAnalysisProgress(message || 'Processing...');
                }
              }
            }
          );

          if (analysis.analysis.needs_followup && analysis.analysis.followup_question) {
            // Show follow-up question
            setFollowupQuestion(analysis.analysis.followup_question);
            // Save the original response for display during follow-up (e.g., image hotspot clicks)
            setOriginalResponse(responseToSubmit);
            // Clear current response for the follow-up text input
            setCurrentResponse('');
            setIsAnalyzing(false);
            setIsSubmitting(false);
            setAnalysisProgress('');
            return; // Don't proceed to next question yet
          }
        } catch (followupError) {
          console.error('Failed to analyze response for follow-up:', followupError);
          setAnalysisProgress('Analysis failed, continuing...');
          // Continue normally if follow-up analysis fails
        } finally {
          setIsAnalyzing(false);
          setAnalysisProgress('');
        }
      }

      // Clear follow-up state and proceed normally
      setFollowupQuestion(null);
      setOriginalResponse(null);

      // Apply pending skip logic (deferred until after follow-ups complete)
      if (skipTarget !== null) {
        console.log(`Applying deferred skip logic: jumping to question index ${skipTarget}`);
        setCurrentResponse('');
        setCurrentQuestionIndex(skipTarget);
        setPendingSkipTarget(null);
        setIsSubmitting(false);
        return;
      }

      // Only advance to next question if shouldAdvanceQuestion was true
      // For MaxDiff/Conjoint intermediate sets, we stay on the current question
      if (shouldAdvanceQuestion) {
        setCurrentResponse(''); // Clear response when advancing

        // Move to next question or complete survey
        if (currentQuestionIndex + 1 < survey.questions.length) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          // All questions answered, complete the survey
          await handleCompleteSession();
        }
      } else {
        // Staying on current question for intermediate sets/scenarios
        // Response is cleared by the setTimeout logic above
      }

    } catch (error) {
      console.error('Failed to submit response:', error);
      setError('Failed to submit response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [session, survey, currentQuestionIndex, currentResponse, followupQuestion, originalResponse, pendingSkipTarget]);

  const handleCompleteSession = useCallback(async () => {
    if (!session) return;

    try {
      setIsSubmitting(true);
      const result = await completeSurveySession(session.id);

      // Check if panel provider redirect URL was provided
      if (result.redirect_url) {
        console.log(`Redirecting to panel provider: ${result.redirect_url}`);
        // Redirect to external panel provider URL
        window.location.href = result.redirect_url;
      } else {
        // No redirect configured, navigate to internal completion page
        navigate(`/survey-complete/${session.id}`);
      }
    } catch (error) {
      console.error('Failed to complete session:', error);
      setError('Failed to complete survey. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [session, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{UI_TEXT.AI_SURVEY.SESSION.DESCRIPTIONS.LOADING}</p>
        </div>
      </div>
    );
  }

  // Don't render question content while skipping conditional questions (prevents flash)
  if (shouldSkipCurrentQuestion || isSkippingConditional) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        </div>
      </div>
    );
  }

  // Show termination screen if session was terminated (check this BEFORE survey validation)
  if (isTerminated) {
    return (
      <SurveyTermination
        reason={terminationReason || undefined}
        redirectUrl={terminationRedirectUrl || undefined}
      />
    );
  }

  if (!session || !survey || !survey.questions) {
    // If there's an error message (e.g., quota reached), show that instead of "Survey Not Found"
    if (error) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center p-6">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Survey Unavailable</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button onClick={() => navigate('/')} variant="primary">Go Home</Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{UI_TEXT.AI_SURVEY.SESSION.TITLES.SURVEY_NOT_FOUND}</h2>
          <p className="text-gray-600 mb-4">{UI_TEXT.AI_SURVEY.SESSION.DESCRIPTIONS.NOT_FOUND}</p>
          <Button onClick={() => navigate('/')}>{UI_TEXT.AI_SURVEY.SESSION.BUTTONS.GO_HOME}</Button>
        </div>
      </div>
    );
  }

  // Show welcome screen if it hasn't been dismissed yet
  if (showWelcomeScreen) {
    const welcomeData = {
      title: survey.title || survey.name || 'Survey',
      description: survey.description || 'Thank you for taking the time to participate in this survey. Our AI will ask follow-up questions based on your answers to gather deeper insights. There are no right or wrong answers - we just want to hear your honest thoughts and opinions.',
      instructions: survey.instructions || 'You can answer using either voice or text input and switch between them at any time. For the best voice experience, please find a quiet space where you can speak clearly.'
    };

    return (
      <SurveyWelcomeScreen
        welcomeData={welcomeData}
        onStart={handleStartSurvey}
      />
    );
  }

  // currentQuestion is computed via useMemo with question_order support
  // If currentQuestion is null, show loading or error state
  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Loading question...</p>
        </div>
      </div>
    );
  }

  const isLastQuestion = currentQuestionIndex === survey.questions.length - 1;

  // Enhanced validation logic for all question types
  const canProceed = (() => {
    if (currentResponse === '' || currentResponse === null || currentResponse === undefined) {
      return false;
    }

    // For follow-up questions, only check if there's a non-empty text response
    if (followupQuestion) {
      return typeof currentResponse === 'string' && currentResponse.trim().length > 0;
    }

    switch (currentQuestion.type) {
      case 'matrix_rating':
        // Matrix questions require all items to have ratings
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        const matrixItems = currentQuestion.matrix_rows || currentQuestion.choices || [];
        return matrixItems.every((item: string) =>
          currentResponse[item] !== undefined && currentResponse[item] !== null
        );

      case 'max_diff':
        // MaxDiff requires both most and least appealing selections
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        return currentResponse.most_appealing && currentResponse.least_appealing;
      
      case 'image_hotspot':
        // Image hotspot requires clicks array with required number of selections
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        const clicks = currentResponse.clicks;
        if (!Array.isArray(clicks)) return false;
        return clicks.length >= (currentQuestion.ranking_count || 3);

      case 'info_screen':
        // Info screens always have a valid response (no input required)
        return true;

      case 'conjoint_analysis':
        // Conjoint analysis requires a profile selection
        return typeof currentResponse === 'string' && currentResponse.startsWith('profile_');

      case 'multiple_choice':
        // If has media URLs in EXPLICIT choice-level mode (legacy), it's an image/video choice question
        if (currentQuestion.media_urls && currentQuestion.media_urls.length > 0 && currentQuestion.media_display_mode === 'choices') {
          return currentResponse?.selected_indices && currentResponse.selected_indices.length > 0;
        }
        // Multiple choice requires at least one selection (handles both question-level media and no media)
        return Array.isArray(currentResponse) && currentResponse.length > 0;

      case 'single_choice':
        // If has media URLs in EXPLICIT choice-level mode (legacy), it's an image/video choice question
        if (currentQuestion.media_urls && currentQuestion.media_urls.length > 0 && currentQuestion.media_display_mode === 'choices') {
          return currentResponse?.selected_indices && currentResponse.selected_indices.length > 0;
        }
        // Single choice requires a selection (handles both question-level media and no media)
        return currentResponse != null;

      case 'ranking':
        // Ranking requires the minimum number of selections
        const rankingCount = currentQuestion.ranking_count || 3;
        return Array.isArray(currentResponse) && currentResponse.length >= rankingCount;

      case 'percentage_allocation':
        // Percentage allocation requires total to equal target
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        const target = currentQuestion.allocation_target || 100;
        const allocOpts = currentQuestion.allocation_options || currentQuestion.choices || [];
        const total = Object.values(currentResponse as Record<string, number>).reduce((sum: number, val) => sum + (val || 0), 0);
        // Check allow_zero constraint - if allow_zero is false, all options must have value > 0
        const allowZeroCheck = currentQuestion.allow_zero !== false; // Default true
        if (!allowZeroCheck && allocOpts.length > 0) {
          const hasZeroValue = allocOpts.some((opt: string) => {
            const val = (currentResponse as Record<string, number>)[opt];
            return val === 0 || val === undefined || val === null;
          });
          if (hasZeroValue) return false;
        }
        return total === target;

      default:
        // For all other question types, basic non-empty validation
        return true;
    }
  })();

  const renderQuestionInput = () => {
    // If there's a follow-up question, always show text input interface
    if (followupQuestion) {
      // Check if the original question has media to display
      // For text questions: always show media if available
      // For single/multiple choice: only show media if NOT in explicit 'choices' mode (legacy)
      // For image_hotspot: always show the reference image
      const isTextQuestion = currentQuestion.type === 'short_text' || currentQuestion.type === 'long_text';
      const isChoiceQuestion = currentQuestion.type === 'single_choice' || currentQuestion.type === 'multiple_choice';
      const isImageHotspot = currentQuestion.type === 'image_hotspot';
      const displayMode = currentQuestion.media_display_mode || 'question'; // Default to question-level for new system
      const isQuestionLevelMedia = displayMode !== 'choices'; // Question-level unless explicitly 'choices'

      // Build image hotspot URL if applicable
      const hotspotImageUrl = isImageHotspot && currentQuestion.image_url
        ? (currentQuestion.image_url.startsWith('http') 
            ? currentQuestion.image_url 
            : `${import.meta.env.VITE_API_URL || ''}/api/question-images/public/${survey.id}/${currentQuestion.image_url.split('/').pop()}`)
        : null;

      const hasMedia = currentQuestion.media_urls && currentQuestion.media_urls.length > 0 &&
        (isTextQuestion || (isChoiceQuestion && isQuestionLevelMedia));
      
      // Get user's selections from originalResponse for image hotspot
      // originalResponse stores the user's response data (e.g., { clicks: [...] } for image_hotspot)
      const hotspotClicks = isImageHotspot && originalResponse && typeof originalResponse === 'object' && originalResponse.clicks
        ? (originalResponse.clicks as Array<{ rank: number; x: number; y: number; product_id: string; product_label: string; timestamp_ms: number }>)
        : [];
      const boundingBoxes = isImageHotspot ? (currentQuestion.bounding_boxes || []) : [];
      
      // Get rank color for visual feedback
      const getRankColor = (rank: number): string => {
        const colors = [
          'bg-teal-500',    // Rank 1
          'bg-blue-500',    // Rank 2
          'bg-purple-500',  // Rank 3
          'bg-orange-500',  // Rank 4
          'bg-pink-500',    // Rank 5
        ];
        return colors[(rank - 1) % colors.length];
      };

      // Image hotspot reference image content with selections overlay
      const hotspotContent = hotspotImageUrl ? (
        <div className="space-y-4 mb-6">
          {/* Reference Image Badge */}
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center rounded-md bg-teal-100 px-2 py-1 text-xs font-medium text-teal-700">
              Reference Image
            </span>
            <span className="text-xs text-gray-500">
              Your selections are highlighted
            </span>
          </div>

          {/* Reference Image with Selections Overlay */}
          <div className="border border-gray-200 rounded-lg p-4 shadow-sm flex flex-col items-center">
            <div className="relative inline-block rounded-md overflow-hidden">
              <img
                src={hotspotImageUrl}
                alt="Reference image for follow-up question"
                className="max-w-full h-auto max-h-[400px] object-contain rounded-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                  }
                }}
              />
              
              {/* Bounding boxes overlay - show selected products */}
              {boundingBoxes.map((box: { id: string; label: string; x: number; y: number; width: number; height: number }) => {
                const isSelected = hotspotClicks.some(click => click.product_id === box.id);
                const clickForBox = hotspotClicks.find(click => click.product_id === box.id);
                
                if (!isSelected) return null; // Only show selected boxes
                
                return (
                  <div
                    key={box.id}
                    className="absolute border-2 border-teal-500 bg-teal-500/20 pointer-events-none"
                    style={{
                      left: `${box.x * 100}%`,
                      top: `${box.y * 100}%`,
                      width: `${box.width * 100}%`,
                      height: `${box.height * 100}%`
                    }}
                  >
                    {/* Rank indicator */}
                    {clickForBox && (
                      <div 
                        className={`absolute -top-3 -left-3 w-6 h-6 ${getRankColor(clickForBox.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md border-2 border-white`}
                      >
                        {clickForBox.rank}
                      </div>
                    )}
                    {/* Product label */}
                    <div className="absolute -bottom-6 left-0 bg-gray-800 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                      {box.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selection Summary */}
          {hotspotClicks.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 mt-4 w-full max-w-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Your Selections:</h4>
              <div className="space-y-1">
                {hotspotClicks.map((click) => (
                  <div key={click.rank} className="flex items-center gap-2 text-sm">
                    <span className={`w-5 h-5 ${getRankColor(click.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center`}>
                      {click.rank}
                    </span>
                    <span className="text-gray-700">
                      {click.product_label}
                    </span>
                    <span className="text-gray-400 text-xs">
                      ({(click.timestamp_ms / 1000).toFixed(1)}s)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null;
      
      const mediaContent = hasMedia ? (
        <div className="space-y-4 mb-6">
          {/* Media Type Badge */}
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
              Follow-up question
            </span>
          </div>

          {/* Media Grid */}
          <div className="grid gap-6 grid-cols-1 max-w-4xl mx-auto">
            {orderedQuestionMediaUrls.map((url: string, index: number) => {
              // Determine if it's image or video
              const isVideo = url && (
                url.includes('youtube.com') ||
                url.includes('youtu.be') ||
                url.includes('vimeo.com') ||
                /\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(url)
              );

              return (
                <div key={`${currentQuestion.id}-media-${index}`} className="border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="relative rounded-md overflow-hidden">
                    {isVideo ? (
                      url.includes('youtube.com') || url.includes('youtu.be') ? (
                        // YouTube Embed
                        <div className="aspect-video">
                          <iframe
                            src={url.includes('youtube.com')
                              ? url.replace('watch?v=', 'embed/')
                              : url.replace('youtu.be/', 'youtube.com/embed/')
                            }
                            className="w-full h-full rounded-md"
                            allowFullScreen
                            title={`Video ${index + 1}`}
                          />
                        </div>
                      ) : (
                        // Regular Video
                        <video
                          src={getFullMediaUrl(url)}
                          className="w-full h-auto max-h-[600px] rounded-md"
                          controls
                          onError={(e) => {
                            const target = e.target as HTMLVideoElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load video</div>';
                            }
                          }}
                        />
                      )
                    ) : (
                      // Image - show full image without cropping
                      <img
                        src={getFullMediaUrl(url)}
                        alt={`Image ${index + 1} for survey question`}
                        className="w-full h-auto max-h-[500px] object-contain rounded-md"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null;

      return (
        <div className="space-y-6">
          {/* Image Hotspot Reference - shown above text input for follow-up questions */}
          {hotspotContent}
          
          {/* Media Content - shown above text input for follow-up questions */}
          {mediaContent}

          {!showTextInput ? (
            // Voice Input Mode (Default)
            <div className="text-center space-y-6">
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  {/* Pulsing ring animation for inactive state */}
                  {!isRecording && !isTranscribing && (
                    <div className="absolute inset-0 w-24 h-24 rounded-full bg-blue-400 animate-ping opacity-20"></div>
                  )}

                  {/* Recording pulse ring */}
                  {isRecording && (
                    <div className="absolute inset-0 w-24 h-24 rounded-full bg-red-400 animate-pulse opacity-30"></div>
                  )}

                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isTranscribing}
                    className={`relative w-24 h-24 rounded-full transition-all duration-300 flex items-center justify-center shadow-lg ${
                      isRecording
                        ? 'bg-red-500 text-white scale-110 shadow-red-200'
                        : isTranscribing
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600 hover:scale-105 active:scale-95 shadow-blue-200'
                    }`}
                    title={isRecording ? 'Click to stop recording' : isTranscribing ? 'Transcribing...' : 'Click to start recording'}
                  >
                  {isTranscribing ? (
                    <div className="w-8 h-8 border-4 border-gray-500 border-t-transparent rounded-full animate-spin" />
                  ) : isRecording ? (
                    /* Real-time audio waveform with flowing wave motion */
                    <div className="flex items-center justify-center space-x-0.5 h-12 w-12 relative">
                      {audioLevels.map((level, i) => {
                        // Start at 1px and scale up with audio
                        const baseHeight = 1 + (level * 17); // More visible variation

                        // Different position variation patterns based on volume level
                        const quietVariation = [0, 3, 6, 9, 6, 3, 0][i]; // Small variations when quiet
                        const loudVariation = [0, 6, 12, 18, 12, 6, 0][i]; // Dramatic variations when loud
                        const positionVariation = level < 1 ? quietVariation : loudVariation;
                        const positionOffset = positionVariation * level;

                        const dynamicHeight = baseHeight + positionOffset;

                        return (
                          <div
                            key={i}
                            className="w-1.5 bg-white rounded-full opacity-95 transition-all duration-75 ease-out"
                            style={{
                              height: `${dynamicHeight}px`,
                            }}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <Mic className="w-8 h-8" />
                  )}
                </button>
                </div>

                <div className="text-center">
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    {isRecording
                      ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.LISTENING
                      : isTranscribing
                      ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.CONVERTING_SPEECH
                      : UI_TEXT.AI_SURVEY.SESSION.MESSAGES.TAP_TO_SPEAK
                    }
                  </p>
                  {!isRecording && !isTranscribing && (
                    <p className="text-sm text-gray-500">
                      {UI_TEXT.AI_SURVEY.SESSION.MESSAGES.VOICE_CONVERTED}
                    </p>
                  )}
                  {(isRecording || isTranscribing) && (
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                      {isRecording && (
                        <>
                          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                          <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.RECORDING_TAP_AGAIN}</span>
                        </>
                      )}
                      {isTranscribing && (
                        <>
                          <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.PROCESSING_SPEECH}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Show transcribed text if available */}
              {currentResponse && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.YOUR_RESPONSE}</p>
                  <p className="text-gray-900">{currentResponse}</p>
                </div>
              )}

              {/* Alternative text input button */}
              <button
                type="button"
                onClick={() => setShowTextInput(true)}
                className="inline-flex items-center space-x-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mt-4"
              >
                <Keyboard className="w-3 h-3" />
                <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.SWITCH_TO_TEXT}</span>
              </button>
            </div>
          ) : (
            // Text Input Mode (Alternative)
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.TYPE_YOUR_ANSWER}</h3>
                <button
                  type="button"
                  onClick={() => setShowTextInput(false)}
                  className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <Mic className="w-4 h-4" />
                  <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.SWITCH_TO_VOICE}</span>
                </button>
              </div>
              <textarea
                value={currentResponse}
                onChange={(e) => handleResponseChange(e.target.value)}
                placeholder={UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OPEN_ENDED}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
                autoFocus
              />
            </div>
          )}
        </div>
      );
    }

    switch (currentQuestion.type) {
      case 'long_text':
      case 'short_text':
        // Check if this is a media text question
        const hasMedia = currentQuestion.media_urls && currentQuestion.media_urls.length > 0;
        const mediaContent = hasMedia ? (
          <div className="space-y-4 mb-6">
            {/* Media Type Badge */}
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                {currentQuestion.type === 'short_text' ? 'Short answer' : 'Long answer'}
              </span>
            </div>

            {/* Media Grid */}
            <div className="grid gap-6 grid-cols-1 max-w-4xl mx-auto">
              {orderedQuestionMediaUrls.map((url: string, index: number) => {
                // Determine if it's image or video
                const isVideo = url && (
                  url.includes('youtube.com') ||
                  url.includes('youtu.be') ||
                  url.includes('vimeo.com') ||
                  /\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(url)
                );

                return (
                  <div key={`${currentQuestion.id}-media-${index}`} className="border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="relative rounded-md overflow-hidden">
                      {isVideo ? (
                        url.includes('youtube.com') || url.includes('youtu.be') ? (
                          // YouTube Embed
                          <div className="aspect-video">
                            <iframe
                              src={url.includes('youtube.com')
                                ? url.replace('watch?v=', 'embed/')
                                : url.replace('youtu.be/', 'youtube.com/embed/')
                              }
                              className="w-full h-full rounded-md"
                              allowFullScreen
                              title={`Video ${index + 1}`}
                            />
                          </div>
                        ) : (
                          // Regular Video
                          <video
                            src={getFullMediaUrl(url)}
                            className="w-full h-auto max-h-[600px] rounded-md"
                            controls
                            onError={(e) => {
                              const target = e.target as HTMLVideoElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load video</div>';
                              }
                            }}
                          />
                        )
                      ) : (
                        // Image - show full image without cropping
                        <img
                          src={getFullMediaUrl(url)}
                          alt={`Image ${index + 1} for survey question`}
                          className="w-full h-auto max-h-[500px] object-contain rounded-md"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;

        return (
          <div className="space-y-6">
            {/* Media Content - shown above text input for media questions */}
            {mediaContent}

            {!showTextInput ? (
              // Voice Input Mode (Default)
              <div className="text-center space-y-6">
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative">
                    {/* Pulsing ring animation for inactive state */}
                    {!isRecording && !isTranscribing && (
                      <div className="absolute inset-0 w-24 h-24 rounded-full bg-blue-400 animate-ping opacity-20"></div>
                    )}
                    
                    {/* Recording pulse ring */}
                    {isRecording && (
                      <div className="absolute inset-0 w-24 h-24 rounded-full bg-red-400 animate-pulse opacity-30"></div>
                    )}

                    <button
                      type="button"
                      onClick={isRecording ? stopRecording : startRecording}
                      disabled={isTranscribing}
                      className={`relative w-24 h-24 rounded-full transition-all duration-300 flex items-center justify-center shadow-lg ${
                        isRecording
                          ? 'bg-red-500 text-white scale-110 shadow-red-200'
                          : isTranscribing
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-500 text-white hover:bg-blue-600 hover:scale-105 active:scale-95 shadow-blue-200'
                      }`}
                      title={isRecording ? 'Click to stop recording' : isTranscribing ? 'Transcribing...' : 'Click to start recording'}
                    >
                    {isTranscribing ? (
                      <div className="w-8 h-8 border-4 border-gray-500 border-t-transparent rounded-full animate-spin" />
                    ) : isRecording ? (
                      /* Real-time audio waveform with flowing wave motion */
                      <div className="flex items-center justify-center space-x-0.5 h-12 w-12 relative">
                        {audioLevels.map((level, i) => {
                          // Start at 1px and scale up with audio
                          const baseHeight = 1 + (level * 17); // More visible variation

                          // Different position variation patterns based on volume level
                          const quietVariation = [0, 3, 6, 9, 6, 3, 0][i]; // Small variations when quiet
                          const loudVariation = [0, 6, 12, 18, 12, 6, 0][i]; // Dramatic variations when loud
                          const positionVariation = level < 1 ? quietVariation : loudVariation;
                          const positionOffset = positionVariation * level;

                          const dynamicHeight = baseHeight + positionOffset;

                          return (
                            <div
                              key={i}
                              className="w-1.5 bg-white rounded-full opacity-95 transition-all duration-75 ease-out"
                              style={{
                                height: `${dynamicHeight}px`,
                              }}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <Mic className="w-8 h-8" />
                    )}
                  </button>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-lg font-medium text-gray-900 mb-2">
                      {isRecording 
                        ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.LISTENING
                        : isTranscribing 
                        ? UI_TEXT.AI_SURVEY.SESSION.MESSAGES.CONVERTING_SPEECH
                        : UI_TEXT.AI_SURVEY.SESSION.MESSAGES.TAP_TO_SPEAK
                      }
                    </p>
                    {!isRecording && !isTranscribing && (
                      <p className="text-sm text-gray-500">
                        {UI_TEXT.AI_SURVEY.SESSION.MESSAGES.VOICE_CONVERTED}
                      </p>
                    )}
                    {(isRecording || isTranscribing) && (
                      <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                        {isRecording && (
                          <>
                            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                            <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.RECORDING_TAP_AGAIN}</span>
                          </>
                        )}
                        {isTranscribing && (
                          <>
                            <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.PROCESSING_SPEECH}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Show transcribed text if available */}
                {currentResponse && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.YOUR_RESPONSE}</p>
                    <p className="text-gray-900">{currentResponse}</p>
                  </div>
                )}

                {/* Alternative text input button */}
                <button
                  type="button"
                  onClick={() => setShowTextInput(true)}
                  className="inline-flex items-center space-x-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mt-4"
                >
                  <Keyboard className="w-3 h-3" />
                  <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.SWITCH_TO_TEXT}</span>
                </button>
              </div>
            ) : (
              // Text Input Mode (Alternative)
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.TYPE_YOUR_ANSWER}</h3>
                  <button
                    type="button"
                    onClick={() => setShowTextInput(false)}
                    className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <Mic className="w-4 h-4" />
                    <span>{UI_TEXT.AI_SURVEY.SESSION.MESSAGES.SWITCH_TO_VOICE}</span>
                  </button>
                </div>
                <textarea
                  value={currentResponse}
                  onChange={(e) => handleResponseChange(e.target.value)}
                  placeholder={currentQuestion.type === 'short_text' ? "Enter your answer..." : UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OPEN_ENDED}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                  autoFocus
                />
              </div>
            )}
          </div>
        );
      
      case 'single_choice':
        // Check if this is a media question and determine display mode
        const hasMediaChoice = currentQuestion.media_urls && currentQuestion.media_urls.length > 0;
        // Only use choice-level mode if EXPLICITLY set to 'choices' (legacy surveys)
        // New system: media_urls = question-level, option_media = per-option
        const displayMode = currentQuestion.media_display_mode || 'question'; // Default to question-level for new system
        const isQuestionMode = displayMode === 'question';

        // Question-level media mode (media displayed below question text)
        if (hasMediaChoice && isQuestionMode) {
          return (
            <div className="space-y-4">
              {/* Media Content Below Question */}
              <div className="space-y-4 mb-6">
                {/* Media Grid */}
                <div className="grid gap-6 grid-cols-1 max-w-4xl mx-auto">
                  {orderedQuestionMediaUrls.map((url: string, index: number) => {
                    // Determine if it's image or video
                    const isVideo = url && (
                      url.includes('youtube.com') ||
                      url.includes('youtu.be') ||
                      url.includes('vimeo.com') ||
                      /\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(url)
                    );

                    return (
                      <div key={`${currentQuestion.id}-media-${index}`} className="border border-gray-200 rounded-lg p-4 shadow-sm">
                        <div className="relative rounded-md overflow-hidden">
                          {isVideo ? (
                            url.includes('youtube.com') || url.includes('youtu.be') ? (
                              // YouTube Embed
                              <div className="aspect-video">
                                <iframe
                                  src={url.includes('youtube.com')
                                    ? url.replace('watch?v=', 'embed/')
                                    : url.replace('youtu.be/', 'youtube.com/embed/')
                                  }
                                  className="w-full h-full rounded-md"
                                  allowFullScreen
                                  title={`Video ${index + 1}`}
                                />
                              </div>
                            ) : (
                              // Regular Video
                              <video
                                src={getFullMediaUrl(url)}
                                className="w-full h-auto max-h-[600px] rounded-md"
                                controls
                                onError={(e) => {
                                  const target = e.target as HTMLVideoElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent) {
                                    parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load video</div>';
                                  }
                                }}
                              />
                            )
                          ) : (
                            // Image - show full image without cropping
                            <img
                              src={getFullMediaUrl(url)}
                              alt={`Image ${index + 1} for survey question`}
                              className="w-full h-auto max-h-[500px] object-contain rounded-md"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Choices — with option_media if present */}
              <div className="space-y-3">
                {randomizedChoices.map((choice: string, index: number) => {
                  const isSelected = currentResponse === choice;
                  const origIdx = currentQuestion.choices?.indexOf(choice) ?? index;
                  const choiceMedia = currentQuestion.option_media?.[String(origIdx)] || currentQuestion.option_media?.[origIdx] || [];

                  if (choiceMedia.length > 0) {
                    return (
                      <button
                        key={choice}
                        type="button"
                        onClick={async () => {
                          handleResponseChange(choice);
                          handleSubmitResponse(choice);
                        }}
                        className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                        <div className={`px-4 py-3 flex items-center justify-between ${isSelected ? 'bg-blue-100' : 'bg-gray-50'}`}>
                          <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{choice}</span>
                          {isSelected && (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <div className={`grid gap-2 ${choiceMedia.length === 1 ? 'grid-cols-1' : choiceMedia.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                            {choiceMedia.map((url: string, mediaIdx: number) => (
                              <div key={mediaIdx} className={`relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100 ${choiceMedia.length === 1 ? 'aspect-video' : 'aspect-square'}`}>
                                <img
                                  src={getFullMediaUrl(url)}
                                  alt={`${choice} media ${mediaIdx + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={choice}
                      type="button"
                      onClick={async () => {
                        handleResponseChange(choice);
                        handleSubmitResponse(choice);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{choice}</span>
                        {isSelected && (
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Other option if enabled */}
                {currentQuestion.allow_other && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        handleResponseChange({ choice: 'Other', other_text: '' });
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        typeof currentResponse === 'object' && currentResponse?.choice === 'Other'
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{UI_TEXT.AI_SURVEY.SESSION.CHOICES.OTHER}</span>
                        {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Text input for Other option */}
                    {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                      <input
                        type="text"
                        placeholder={UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                        value={currentResponse?.other_text || ''}
                        onChange={(e) => {
                          handleResponseChange({
                            choice: 'Other',
                            other_text: e.target.value
                          });
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        }

        // Choice-level media mode (media displayed with each choice) - existing/legacy behavior
        // Only use this if option_media is NOT present (option_media is the new preferred approach)
        const hasNewOptionMedia = currentQuestion.option_media && Object.keys(currentQuestion.option_media).length > 0;
        if (hasMediaChoice && !isQuestionMode && !hasNewOptionMedia) {
          const displayUrls = orderedQuestionMediaUrls;
          const storageUrls = currentQuestion.media_urls || [];

          return (
            <div className="space-y-4">
              {/* Instructions */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Click on an image to select it.
                </p>
              </div>

              {/* Image Grid */}
              <div className={`grid gap-6 ${
                displayUrls.length === 1
                  ? 'grid-cols-1 max-w-2xl mx-auto'
                  : displayUrls.length === 2
                  ? 'grid-cols-1 lg:grid-cols-2 max-w-4xl mx-auto'
                  : 'grid-cols-1 lg:grid-cols-2 max-w-5xl mx-auto'
              }`}>
                {displayUrls.map((url: string, displayIdx: number) => {
                  const originalIdx = questionMediaOriginalIndices[displayIdx];
                  const isSelected =
                    currentResponse?.selected_indices?.includes(originalIdx) ||
                    currentResponse?.selected_index === originalIdx;

                  return (
                    <div
                      key={`${currentQuestion.id}-image-${originalIdx}`}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-lg border-2 rounded-lg p-4 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                      }`}
                      onClick={() => {
                        const response = {
                          selected_indices: [originalIdx],
                          selected_urls: [storageUrls[originalIdx]],
                          selected_index: originalIdx
                        };
                        handleResponseChange(response);
                        handleSubmitResponse(response);
                      }}
                    >
                      {/* Selection Indicator */}
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {currentQuestion.choices?.[originalIdx] || `Image ${originalIdx + 1}`}
                        </span>
                        {isSelected && (
                          <Check className="h-5 w-5 text-blue-500" />
                        )}
                      </div>

                      {/* Image Container */}
                      <div className="relative rounded-md overflow-hidden">
                        <img
                          src={getFullMediaUrl(url)}
                          alt={`Image ${originalIdx + 1} - Choice option`}
                          className="w-full h-auto max-h-80 object-contain rounded-md"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-32 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                            }
                          }}
                        />

                        {/* Selection Overlay */}
                        {isSelected && (
                          <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                            <Check className="h-8 w-8 text-white drop-shadow-md" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        // Check if any options have option_media
        const hasOptionMedia = currentQuestion.option_media && Object.keys(currentQuestion.option_media).length > 0;
        const allOptionMediaLoaded = !hasOptionMedia || loadedOptionMediaCount >= totalOptionMedia;

        // If we have option_media AND media_urls, render media_urls as question-level media
        const questionLevelMedia = hasOptionMedia && hasMediaChoice ? orderedQuestionMediaUrls : null;

        return (
          <div className="space-y-3">
            {/* Render question-level media if present */}
            {questionLevelMedia && questionLevelMedia.length > 0 && (
              <div className="mb-6">
                <div className="grid gap-4 grid-cols-1 max-w-3xl mx-auto">
                  {questionLevelMedia.map((url: string, mediaIdx: number) => {
                    const isVideo = url && (
                      url.includes('youtube.com') ||
                      url.includes('youtu.be') ||
                      url.includes('vimeo.com') ||
                      /\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(url)
                    );
                    return (
                      <div key={`q-media-${mediaIdx}`} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        {isVideo ? (
                          <video
                            src={getFullMediaUrl(url)}
                            className="w-full h-auto max-h-[400px]"
                            controls
                          />
                        ) : (
                          <img
                            src={getFullMediaUrl(url)}
                            alt={`Question media ${mediaIdx + 1}`}
                            className="w-full h-auto max-h-[400px] object-contain"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {randomizedChoices.map((choice: string, index: number) => {
              const isSelected = currentResponse === choice;
              // Get the original index for option_media lookup (before randomization)
              const originalIndex = currentQuestion.choices?.indexOf(choice) ?? index;
              const optionMediaUrls = currentQuestion.option_media?.[String(originalIndex)] || currentQuestion.option_media?.[originalIndex] || [];
              
              // Render differently based on whether option has media
              if (optionMediaUrls.length > 0) {
                // Card-style layout for options with media
                return (
                  <button
                    key={choice}
                    type="button"
                    disabled={hasOptionMedia && !allOptionMediaLoaded}
                    onClick={async () => {
                      if (hasOptionMedia && !allOptionMediaLoaded) return;
                      handleResponseChange(choice);
                      handleSubmitResponse(choice);
                    }}
                    className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                      hasOptionMedia && !allOptionMediaLoaded
                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {/* Option header with choice text */}
                    <div className={`px-4 py-3 flex items-center justify-between ${
                      isSelected ? 'bg-blue-100' : 'bg-gray-50'
                    }`}>
                      <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                        {choice}
                      </span>
                      {isSelected && (
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                    
                    {/* Media grid */}
                    <div className="p-3">
                      <div className={`grid gap-2 ${
                        optionMediaUrls.length === 1 
                          ? 'grid-cols-1' 
                          : optionMediaUrls.length === 2 
                            ? 'grid-cols-2' 
                            : 'grid-cols-3'
                      }`}>
                        {optionMediaUrls.map((url: string, mediaIndex: number) => {
                          const mediaKey = `${originalIndex}-${mediaIndex}`;
                          const isLoaded = optionMediaLoaded[mediaKey];
                          
                          return (
                            <div 
                              key={mediaIndex} 
                              className={`relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100 ${
                                optionMediaUrls.length === 1 ? 'aspect-video' : 'aspect-square'
                              }`}
                            >
                              {!isLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
                                  <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                              <img
                                src={getFullMediaUrl(url)}
                                alt={`${choice} media ${mediaIndex + 1}`}
                                className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                                onLoad={() => {
                                  setOptionMediaLoaded(prev => ({ ...prev, [mediaKey]: true }));
                                  setLoadedOptionMediaCount(prev => prev + 1);
                                }}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  setOptionMediaLoaded(prev => ({ ...prev, [mediaKey]: true }));
                                  setLoadedOptionMediaCount(prev => prev + 1);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </button>
                );
              }
              
              // Standard layout for options without media
              return (
                <button
                  key={choice}
                  type="button"
                  onClick={async () => {
                    handleResponseChange(choice);
                    handleSubmitResponse(choice);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    {isSelected && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
            
            {/* Other option if enabled */}
            {currentQuestion.allow_other && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    handleResponseChange({ choice: 'Other', other_text: '' });
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    typeof currentResponse === 'object' && currentResponse?.choice === 'Other'
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{UI_TEXT.AI_SURVEY.SESSION.CHOICES.OTHER}</span>
                    {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
                
                {/* Text input for Other option */}
                {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                  <input
                    type="text"
                    placeholder={UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                    value={currentResponse?.other_text || ''}
                    onChange={(e) => {
                      handleResponseChange({
                        choice: 'Other',
                        other_text: e.target.value
                      });
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                )}
              </div>
            )}
          </div>
        );
      
      case 'multiple_choice':
        // Check if this is a media question and determine display mode
        const hasMediaMultiple = currentQuestion.media_urls && currentQuestion.media_urls.length > 0;
        // Only use choice-level mode if EXPLICITLY set to 'choices' (legacy surveys)
        // New system: media_urls = question-level, option_media = per-option
        const displayModeMultiple = currentQuestion.media_display_mode || 'question'; // Default to question-level for new system
        const isQuestionModeMultiple = displayModeMultiple === 'question';

        // Question-level media mode (media displayed below question text)
        if (hasMediaMultiple && isQuestionModeMultiple) {
          return (
            <div className="space-y-4">
              {/* Media Content Below Question */}
              <div className="space-y-4 mb-6">
                {/* Media Grid */}
                <div className="grid gap-6 grid-cols-1 max-w-4xl mx-auto">
                  {orderedQuestionMediaUrls.map((url: string, index: number) => {
                    // Determine if it's image or video
                    const isVideo = url && (
                      url.includes('youtube.com') ||
                      url.includes('youtu.be') ||
                      url.includes('vimeo.com') ||
                      /\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(url)
                    );

                    return (
                      <div key={`${currentQuestion.id}-media-${index}`} className="border border-gray-200 rounded-lg p-4 shadow-sm">
                        <div className="relative rounded-md overflow-hidden">
                          {isVideo ? (
                            url.includes('youtube.com') || url.includes('youtu.be') ? (
                              // YouTube Embed
                              <div className="aspect-video">
                                <iframe
                                  src={url.includes('youtube.com')
                                    ? url.replace('watch?v=', 'embed/')
                                    : url.replace('youtu.be/', 'youtube.com/embed/')
                                  }
                                  className="w-full h-full rounded-md"
                                  allowFullScreen
                                  title={`Video ${index + 1}`}
                                />
                              </div>
                            ) : (
                              // Regular Video
                              <video
                                src={getFullMediaUrl(url)}
                                className="w-full h-auto max-h-[600px] rounded-md"
                                controls
                                onError={(e) => {
                                  const target = e.target as HTMLVideoElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent) {
                                    parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load video</div>';
                                  }
                                }}
                              />
                            )
                          ) : (
                            // Image - show full image without cropping
                            <img
                              src={getFullMediaUrl(url)}
                              alt={`Image ${index + 1} for survey question`}
                              className="w-full h-auto max-h-[500px] object-contain rounded-md"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  parent.innerHTML = '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Choices — with option_media if present */}
              <div className="space-y-3">
                {randomizedChoices.map((choice: string, index: number) => {
                  const responses = Array.isArray(currentResponse) ? currentResponse : [];
                  const isSelected = responses.some((item: any) =>
                    typeof item === 'string' ? item === choice : item?.choice === choice
                  );
                  const origIdx = currentQuestion.choices?.indexOf(choice) ?? index;
                  const choiceMedia = currentQuestion.option_media?.[String(origIdx)] || currentQuestion.option_media?.[origIdx] || [];

                  const toggleChoice = () => {
                    const currentResponses = Array.isArray(currentResponse) ? currentResponse : [];
                    if (isSelected) {
                      handleResponseChange(currentResponses.filter((item: any) =>
                        typeof item === 'string' ? item !== choice : item?.choice !== choice
                      ));
                    } else {
                      handleResponseChange([...currentResponses, choice]);
                    }
                  };

                  if (choiceMedia.length > 0) {
                    return (
                      <button
                        key={choice}
                        type="button"
                        onClick={toggleChoice}
                        className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                        <div className={`px-4 py-3 flex items-center justify-between ${isSelected ? 'bg-blue-100' : 'bg-gray-50'}`}>
                          <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>{choice}</span>
                          {isSelected && (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <div className={`grid gap-2 ${choiceMedia.length === 1 ? 'grid-cols-1' : choiceMedia.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                            {choiceMedia.map((url: string, mediaIdx: number) => (
                              <div key={mediaIdx} className={`relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100 ${choiceMedia.length === 1 ? 'aspect-video' : 'aspect-square'}`}>
                                <img
                                  src={getFullMediaUrl(url)}
                                  alt={`${choice} media ${mediaIdx + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={choice}
                      type="button"
                      onClick={toggleChoice}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{choice}</span>
                        {isSelected && (
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Other option if enabled */}
                {currentQuestion.allow_other && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        const responses = Array.isArray(currentResponse) ? currentResponse : [];
                        const otherExists = responses.some((item: any) =>
                          typeof item === 'object' && item?.choice === 'Other'
                        );

                        if (otherExists) {
                          // Remove Other option
                          handleResponseChange(responses.filter((item: any) =>
                            !(typeof item === 'object' && item?.choice === 'Other')
                          ));
                        } else {
                          // Add Other option
                          handleResponseChange([...responses, { choice: 'Other', other_text: '' }]);
                        }
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        Array.isArray(currentResponse) && currentResponse.some((item: any) =>
                          typeof item === 'object' && item?.choice === 'Other'
                        )
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{UI_TEXT.AI_SURVEY.SESSION.CHOICES.OTHER}</span>
                        {Array.isArray(currentResponse) && currentResponse.some((item: any) =>
                          typeof item === 'object' && item?.choice === 'Other'
                        ) && (
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Text input for Other option */}
                    {Array.isArray(currentResponse) && currentResponse.some((item: any) =>
                      typeof item === 'object' && item?.choice === 'Other'
                    ) && (
                      <input
                        type="text"
                        placeholder={UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                        value={(() => {
                          const otherItem = currentResponse.find((item: any) =>
                            typeof item === 'object' && item?.choice === 'Other'
                          );
                          return otherItem?.other_text || '';
                        })()}
                        onChange={(e) => {
                          const responses = Array.isArray(currentResponse) ? currentResponse : [];
                          const updatedResponses = responses.map((item: any) =>
                            typeof item === 'object' && item?.choice === 'Other'
                              ? { choice: 'Other', other_text: e.target.value }
                              : item
                          );
                          handleResponseChange(updatedResponses);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        }

        // Choice-level media mode (media displayed with each choice) - existing/legacy behavior
        // Only use this if option_media is NOT present (option_media is the new preferred approach)
        const hasNewOptionMediaMultiple = currentQuestion.option_media && Object.keys(currentQuestion.option_media).length > 0;
        if (hasMediaMultiple && !isQuestionModeMultiple && !hasNewOptionMediaMultiple) {
          const displayUrls = orderedQuestionMediaUrls;
          const storageUrls = currentQuestion.media_urls || [];
          const selectedIndices = currentResponse?.selected_indices || [];

          return (
            <div className="space-y-4">
              {/* Instructions */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  You can select multiple images by clicking on them.
                </p>
                <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                  Select multiple
                </span>
              </div>

              {/* Image Grid */}
              <div className={`grid gap-6 ${
                displayUrls.length === 1
                  ? 'grid-cols-1 max-w-2xl mx-auto'
                  : displayUrls.length === 2
                  ? 'grid-cols-1 lg:grid-cols-2 max-w-4xl mx-auto'
                  : 'grid-cols-1 lg:grid-cols-2 max-w-5xl mx-auto'
              }`}>
                {displayUrls.map((url: string, displayIdx: number) => {
                  const originalIdx = questionMediaOriginalIndices[displayIdx];
                  const isSelected = selectedIndices.includes(originalIdx);

                  return (
                    <div
                      key={`${currentQuestion.id}-image-${originalIdx}`}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-lg border-2 rounded-lg p-4 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                      }`}
                      onClick={() => {
                        let newSelectedIndices: number[];
                        if (isSelected) {
                          newSelectedIndices = selectedIndices.filter((i: number) => i !== originalIdx);
                        } else {
                          newSelectedIndices = [...selectedIndices, originalIdx];
                        }

                        const response = {
                          selected_indices: newSelectedIndices,
                          selected_urls: newSelectedIndices.map((i: number) => storageUrls[i])
                        };
                        handleResponseChange(response);
                      }}
                    >
                      {/* Selection Indicator */}
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {currentQuestion.choices?.[originalIdx] || `Image ${originalIdx + 1}`}
                        </span>
                        {isSelected && (
                          <Check className="h-5 w-5 text-blue-500" />
                        )}
                      </div>

                      {/* Image Container */}
                      <div className="relative rounded-md overflow-hidden">
                        <img
                          src={getFullMediaUrl(url)}
                          alt={`Image ${originalIdx + 1} - Choice option`}
                          className="w-full h-auto max-h-80 object-contain rounded-md"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-32 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                            }
                          }}
                        />

                        {/* Selection Overlay */}
                        {isSelected && (
                          <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                            <Check className="h-8 w-8 text-white drop-shadow-md" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        // Check if any options have option_media for multiple choice
        const hasOptionMediaMultiple = currentQuestion.option_media && Object.keys(currentQuestion.option_media).length > 0;
        const allOptionMediaLoadedMultiple = !hasOptionMediaMultiple || loadedOptionMediaCount >= totalOptionMedia;

        // If we have option_media AND media_urls, render media_urls as question-level media
        const questionLevelMediaMultiple = hasOptionMediaMultiple && hasMediaMultiple ? orderedQuestionMediaUrls : null;

        return (
          <div className="space-y-3">
            {/* Render question-level media if present */}
            {questionLevelMediaMultiple && questionLevelMediaMultiple.length > 0 && (
              <div className="mb-6">
                <div className="grid gap-4 grid-cols-1 max-w-3xl mx-auto">
                  {questionLevelMediaMultiple.map((url: string, mediaIdx: number) => {
                    const isVideo = url && (
                      url.includes('youtube.com') ||
                      url.includes('youtu.be') ||
                      url.includes('vimeo.com') ||
                      /\.(mp4|mov|avi|webm|mkv)(\?|$)/i.test(url)
                    );
                    return (
                      <div key={`q-media-${mediaIdx}`} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        {isVideo ? (
                          <video
                            src={getFullMediaUrl(url)}
                            className="w-full h-auto max-h-[400px]"
                            controls
                          />
                        ) : (
                          <img
                            src={getFullMediaUrl(url)}
                            alt={`Question media ${mediaIdx + 1}`}
                            className="w-full h-auto max-h-[400px] object-contain"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {randomizedChoices.map((choice: string, index: number) => {
              const responses = Array.isArray(currentResponse) ? currentResponse : [];
              const isSelected = responses.some((item: any) =>
                typeof item === 'string' ? item === choice : item?.choice === choice
              );
              // Get the original index for option_media lookup (before randomization)
              const originalIndexMultiple = currentQuestion.choices?.indexOf(choice) ?? index;
              const optionMediaUrlsMultiple = currentQuestion.option_media?.[String(originalIndexMultiple)] || currentQuestion.option_media?.[originalIndexMultiple] || [];
              
              // Render card-style for options with media
              if (optionMediaUrlsMultiple.length > 0) {
                return (
                  <button
                    key={choice}
                    type="button"
                    disabled={hasOptionMediaMultiple && !allOptionMediaLoadedMultiple}
                    onClick={() => {
                      if (hasOptionMediaMultiple && !allOptionMediaLoadedMultiple) return;
                      const currentResponses = Array.isArray(currentResponse) ? currentResponse : [];
                      if (isSelected) {
                        handleResponseChange(currentResponses.filter((item: any) =>
                          typeof item === 'string' ? item !== choice : item?.choice !== choice
                        ));
                      } else {
                        handleResponseChange([...currentResponses, choice]);
                      }
                    }}
                    className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                      hasOptionMediaMultiple && !allOptionMediaLoadedMultiple
                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {/* Option header with choice text */}
                    <div className={`px-4 py-3 flex items-center justify-between ${
                      isSelected ? 'bg-blue-100' : 'bg-gray-50'
                    }`}>
                      <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                        {choice}
                      </span>
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                    
                    {/* Media grid */}
                    <div className="p-3">
                      <div className={`grid gap-2 ${
                        optionMediaUrlsMultiple.length === 1 
                          ? 'grid-cols-1' 
                          : optionMediaUrlsMultiple.length === 2 
                            ? 'grid-cols-2' 
                            : 'grid-cols-3'
                      }`}>
                        {optionMediaUrlsMultiple.map((url: string, mediaIndex: number) => {
                          const mediaKey = `${originalIndexMultiple}-${mediaIndex}`;
                          const isLoaded = optionMediaLoaded[mediaKey];
                          
                          return (
                            <div 
                              key={mediaIndex} 
                              className={`relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100 ${
                                optionMediaUrlsMultiple.length === 1 ? 'aspect-video' : 'aspect-square'
                              }`}
                            >
                              {!isLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
                                  <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                              <img
                                src={getFullMediaUrl(url)}
                                alt={`${choice} media ${mediaIndex + 1}`}
                                className={`w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                                onLoad={() => {
                                  setOptionMediaLoaded(prev => ({ ...prev, [mediaKey]: true }));
                                  setLoadedOptionMediaCount(prev => prev + 1);
                                }}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  setOptionMediaLoaded(prev => ({ ...prev, [mediaKey]: true }));
                                  setLoadedOptionMediaCount(prev => prev + 1);
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </button>
                );
              }
              
              // Standard layout for options without media
              return (
                <button
                  key={choice}
                  type="button"
                  onClick={() => {
                    const currentResponses = Array.isArray(currentResponse) ? currentResponse : [];
                    if (isSelected) {
                      handleResponseChange(currentResponses.filter((item: any) =>
                        typeof item === 'string' ? item !== choice : item?.choice !== choice
                      ));
                    } else {
                      handleResponseChange([...currentResponses, choice]);
                    }
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </button>
              );
            })}
            
            {/* Other option if enabled */}
            {currentQuestion.allow_other && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    const responses = Array.isArray(currentResponse) ? currentResponse : [];
                    const otherExists = responses.some((item: any) => 
                      typeof item === 'object' && item?.choice === 'Other'
                    );
                    
                    if (otherExists) {
                      // Remove Other option
                      handleResponseChange(responses.filter((item: any) => 
                        !(typeof item === 'object' && item?.choice === 'Other')
                      ));
                    } else {
                      // Add Other option
                      handleResponseChange([...responses, { choice: 'Other', other_text: '' }]);
                    }
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    Array.isArray(currentResponse) && currentResponse.some((item: any) => 
                      typeof item === 'object' && item?.choice === 'Other'
                    )
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{UI_TEXT.AI_SURVEY.SESSION.CHOICES.OTHER}</span>
                    {Array.isArray(currentResponse) && currentResponse.some((item: any) => 
                      typeof item === 'object' && item?.choice === 'Other'
                    ) && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
                
                {/* Text input for Other option */}
                {Array.isArray(currentResponse) && currentResponse.some((item: any) => 
                  typeof item === 'object' && item?.choice === 'Other'
                ) && (
                  <input
                    type="text"
                    placeholder={UI_TEXT.AI_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                    value={(() => {
                      const otherItem = currentResponse.find((item: any) => 
                        typeof item === 'object' && item?.choice === 'Other'
                      );
                      return otherItem?.other_text || '';
                    })()}
                    onChange={(e) => {
                      const responses = Array.isArray(currentResponse) ? currentResponse : [];
                      const updatedResponses = responses.map((item: any) => 
                        typeof item === 'object' && item?.choice === 'Other'
                          ? { choice: 'Other', other_text: e.target.value }
                          : item
                      );
                      handleResponseChange(updatedResponses);
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                )}
              </div>
            )}
          </div>
        );

      case 'yes_no':
        return (
          <div className="space-y-3">
            {(currentQuestion.choices || ['Yes', 'No']).map((choice: string, index: number) => {
              const isSelected = currentResponse === choice;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={async () => {
                    handleResponseChange(choice);
                    handleSubmitResponse(choice);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    {isSelected && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 'rating_scale':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <div className="flex justify-center space-x-2">
                {Array.from({ length: (currentQuestion.scale_max || 5) - (currentQuestion.scale_min || 1) + 1 }, (_, index) => {
                  const value = (currentQuestion.scale_min || 1) + index;
                  const isSelected = currentResponse === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        handleResponseChange(value);
                        handleSubmitResponse(value);
                      }}
                      className={`w-12 h-12 rounded-full border-2 font-semibold transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>

              {/* Scale labels */}
              <div className="flex items-center justify-between text-xs text-gray-500 mt-3 max-w-xs mx-auto">
                <span className="text-left">Not at all</span>
                <span className="text-right">Extremely</span>
              </div>
            </div>
          </div>
        );

      case 'likert_scale':
        return (
          <div className="space-y-3">
            {[
              'Strongly Disagree',
              'Disagree',
              'Neutral',
              'Agree',
              'Strongly Agree'
            ].map((choice: string, index: number) => {
              const isSelected = currentResponse === choice;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={async () => {
                    handleResponseChange(choice);
                    handleSubmitResponse(choice);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    {isSelected && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 'dropdown':
        return (
          <div className="space-y-4">
            <select
              value={currentResponse || ''}
              onChange={(e) => {
                handleResponseChange(e.target.value);
                handleSubmitResponse(e.target.value);
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            >
              <option value="">Select an option...</option>
              {currentQuestion.choices?.map((choice: string, index: number) => (
                <option key={index} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </div>
        );

      case 'nps':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-4">
                On a scale of 0-10, how likely are you to recommend this to a friend or colleague?
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                <span>Not at all likely</span>
                <span>Extremely likely</span>
              </div>
              <div className="flex justify-center space-x-1">
                {Array.from({ length: 11 }, (_, index) => {
                  const value = index;
                  const isSelected = currentResponse === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        handleResponseChange(value);
                        handleSubmitResponse(value);
                      }}
                      className={`w-10 h-10 rounded border-2 font-semibold text-sm transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'numeric_input':
        return (
          <div className="space-y-4">
            <input
              type="number"
              value={currentResponse || ''}
              onChange={(e) => handleResponseChange(e.target.value)}
              placeholder="Enter a number..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>
        );

      case 'ranking':
        return (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              Click to select, then drag to reorder your top {currentQuestion.ranking_count || 3} choices:
            </div>

            {/* Ranked items (draggable) */}
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Your Rankings:</div>
              <div className="space-y-2 min-h-[60px] p-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                {Array.isArray(currentResponse) && currentResponse.length > 0 ? (
                  currentResponse.map((choice: string, index: number) => (
                    <div
                      key={choice}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', index.toString());
                        e.currentTarget.style.opacity = '0.5';
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                        const droppedIndex = index;

                        if (draggedIndex !== droppedIndex) {
                          const newRankings = Array.isArray(currentResponse) ? [...currentResponse] : [];
                          const draggedItem = newRankings[draggedIndex];
                          newRankings.splice(draggedIndex, 1);
                          newRankings.splice(droppedIndex, 0, draggedItem);
                          handleResponseChange(newRankings);
                        }
                      }}
                      className="flex items-center justify-between px-4 py-3 bg-blue-50 border-2 border-blue-200 rounded-lg cursor-move hover:bg-blue-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                          {index + 1}
                        </div>
                        <span className="text-blue-900 font-medium">{choice}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                        </svg>
                        <button
                          type="button"
                          onClick={() => {
                            const newRankings = Array.isArray(currentResponse) ? [...currentResponse] : [];
                            newRankings.splice(index, 1);
                            handleResponseChange(newRankings);
                          }}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    Select items below to start ranking
                  </div>
                )}
              </div>
            </div>

            {/* Available choices */}
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Available Choices:</div>
              <div className="space-y-2">
                {currentQuestion.choices?.map((choice: string, index: number) => {
                  const rankings = Array.isArray(currentResponse) ? currentResponse : [];
                  const isRanked = rankings.includes(choice);
                  const maxRank = currentQuestion.ranking_count || 3;
                  const canAdd = rankings.length < maxRank;

                  if (isRanked) return null; // Don't show already ranked items

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        if (canAdd) {
                          const newRankings = Array.isArray(currentResponse) ? [...currentResponse] : [];
                          newRankings.push(choice);
                          handleResponseChange(newRankings);
                        }
                      }}
                      disabled={!canAdd}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        canAdd
                          ? 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{choice}</span>
                        {canAdd && (
                          <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="text-xs text-gray-500 mt-2">
              Selected: {Array.isArray(currentResponse) ? currentResponse.length : 0} of {currentQuestion.ranking_count || 3}
            </div>
          </div>
        );

      case 'matrix_rating':
        // Use randomized rows if randomization is enabled, otherwise use original order
        const matrixRows = randomizedMatrixRows.length > 0 ? randomizedMatrixRows : (currentQuestion.matrix_rows || currentQuestion.choices || []);

        return (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-3 border border-gray-300 font-medium text-gray-700 min-w-[200px]">
                      {/* Empty header for row labels */}
                    </th>
                    {(currentQuestion.matrix_columns || ['1', '2', '3', '4', '5']).map((label, index) => (
                      <th key={index} className="text-center p-3 border border-gray-300 font-medium text-gray-700 min-w-[80px]">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixRows.map((item, itemIndex) => (
                    <tr key={itemIndex} className="hover:bg-gray-50">
                      <td className="p-3 border border-gray-300 font-medium text-gray-900">
                        {item}
                      </td>
                      {(currentQuestion.matrix_columns || ['1', '2', '3', '4', '5']).map((label, scaleIndex) => {
                        const scaleValue = currentQuestion.scale_min ? currentQuestion.scale_min + scaleIndex : scaleIndex + 1;
                        const matrixResponse = currentResponse || {};
                        const isSelected = matrixResponse[item] === scaleValue;

                        return (
                          <td key={scaleIndex} className="p-3 border border-gray-300 text-center">
                            <input
                              type="radio"
                              name={`matrix_${itemIndex}`}
                              value={scaleValue}
                              checked={isSelected}
                              onChange={() => {
                                const newResponse = { ...matrixResponse };
                                newResponse[item] = scaleValue;
                                handleResponseChange(newResponse);
                              }}
                              className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'max_diff':
        // Generate balanced sets using user-seeded randomization
        const maxDiffSets = session ? generateAndPrepareSetsForUser(currentQuestion.choices || [], session.id.toString(), 4, 4) : [];

        // Evaluate and log the design quality for this session
        if (session && maxDiffSets.length > 0) {
          const evaluation = evaluateMaxDiffDesign(maxDiffSets, currentQuestion.choices || []);
          console.log(`[MaxDiff Evaluation - Session ${session.id} - Render]`, evaluation);
        }

        const currentSet = maxDiffSets[currentMaxDiffSetIndex] || [];
        const totalSets = maxDiffSets.length;

        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="text-sm text-gray-500 mb-2">
                Set {currentMaxDiffSetIndex + 1} of {totalSets}
              </div>
              <div className="text-xs text-gray-600">
                Select the most and least appealing options • No right or wrong answers
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Most Appealing Section */}
              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <h3 className="text-lg font-semibold text-green-800 mb-3 text-center">Most Appealing</h3>
                <div className="space-y-2">
                  {currentSet.map((choice, index) => {
                    const response = currentResponse || {};
                    const isSelected = response.most_appealing === choice;
                    const isDisabled = response.least_appealing === choice;

                    return (
                      <button
                        key={index}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          const newResponse = { ...response };
                          newResponse.most_appealing = choice;
                          handleResponseChange(newResponse);
                        }}
                        className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-green-500 bg-green-100 text-green-900'
                            : isDisabled
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'border-gray-300 bg-white text-gray-900 hover:border-green-400 hover:bg-green-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{choice}</span>
                          {isSelected && (
                            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Least Appealing Section */}
              <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                <h3 className="text-lg font-semibold text-red-800 mb-3 text-center">Least Appealing</h3>
                <div className="space-y-2">
                  {currentSet.map((choice, index) => {
                    const response = currentResponse || {};
                    const isSelected = response.least_appealing === choice;
                    const isDisabled = response.most_appealing === choice;

                    return (
                      <button
                        key={index}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          const newResponse = { ...response };
                          newResponse.least_appealing = choice;
                          handleResponseChange(newResponse);
                        }}
                        className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-red-500 bg-red-100 text-red-900'
                            : isDisabled
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'border-gray-300 bg-white text-gray-900 hover:border-red-400 hover:bg-red-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{choice}</span>
                          {isSelected && (
                            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );

      case 'conjoint_analysis':
        return (
          <div className="space-y-6">
            {(() => {
              const attributes = currentQuestion.conjoint_attributes || [];

              if (attributes.length === 0) {
                return (
                  <div className="text-center text-gray-500 py-8">
                    No attributes configured for this conjoint analysis question.
                  </div>
                );
              }

              // Advanced conjoint design algorithm
              const generateOrthogonalDesign = () => {
                // Calculate optimal number of profiles for statistical robustness
                const totalLevels = attributes.reduce((sum, attr) => sum + attr.levels.length, 0);
                const maxLevels = Math.max(...attributes.map(attr => attr.levels.length));

                // Use statistical rules for conjoint design
                const minProfilesNeeded = Math.max(
                  totalLevels, // At least as many profiles as total levels
                  attributes.length * 2, // At least 2x number of attributes
                  12 // Minimum for reliable utility estimation
                );

                // Generate balanced orthogonal array
                const profiles = [];
                const seed = 42; // For reproducible designs per question

                // Create systematic orthogonal combinations
                for (let i = 0; i < minProfilesNeeded; i++) {
                  const profile = {};

                  attributes.forEach((attr, attrIndex) => {
                    // Use balanced orthogonal rotation patterns
                    // Each attribute gets a different but reasonable rotation pattern
                    const rotationMultiplier = attrIndex + 1; // 1, 2, 3, 4, etc. (linear not exponential)
                    const levelIndex = Math.floor(i / rotationMultiplier) % attr.levels.length;
                    profile[attr.name] = attr.levels[levelIndex];
                  });

                  profiles.push({
                    id: `profile_${i + 1}`,
                    attributes: profile
                  });
                }

                // Ensure balanced representation of all levels
                const balancedProfiles = balanceAttributeLevels(profiles, attributes);

                return balancedProfiles;
              };

              // Balance attribute levels across profiles for statistical validity
              const balanceAttributeLevels = (profiles, attributes) => {
                const balanced = [...profiles];

                attributes.forEach(attr => {
                  const levelCounts = {};
                  attr.levels.forEach(level => levelCounts[level] = 0);

                  // Count current level usage
                  balanced.forEach(profile => {
                    levelCounts[profile.attributes[attr.name]]++;
                  });

                  // Rebalance underrepresented levels
                  const minCount = Math.min(...Object.values(levelCounts));
                  const maxCount = Math.max(...Object.values(levelCounts));

                  if (maxCount - minCount > 1) {
                    // Find underrepresented levels and swap them in
                    const underrepresented = attr.levels.filter(level =>
                      levelCounts[level] === minCount
                    );

                    let swapIndex = 0;
                    for (let i = 0; i < balanced.length && underrepresented.length > 0; i++) {
                      const currentLevel = balanced[i].attributes[attr.name];
                      if (levelCounts[currentLevel] > minCount + 1) {
                        balanced[i].attributes[attr.name] = underrepresented[swapIndex];
                        levelCounts[currentLevel]--;
                        levelCounts[underrepresented[swapIndex]]++;
                        swapIndex = (swapIndex + 1) % underrepresented.length;
                      }
                    }
                  }
                });

                return balanced;
              };

              // Generate choice task (3 profiles per task is optimal)
              const profilesPerTask = 3;
              const allProfiles = generateOrthogonalDesign();

              // Get current task index from state
              const currentTaskIndex = currentConjointScenarioIndex;

              // Select profiles for current choice task using systematic rotation
              const taskProfiles = [];
              for (let i = 0; i < profilesPerTask; i++) {
                const profileIndex = (currentTaskIndex * profilesPerTask + i) % allProfiles.length;
                taskProfiles.push(allProfiles[profileIndex]);
              }

              return (
                <div className="space-y-4">
                  {/* Choice task header */}
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Which option would you be most likely to choose?
                    </h3>
                    <p className="text-sm text-gray-600">
                      Select the one option that best meets your preferences
                    </p>
                    <div className="text-sm text-gray-500 mt-2">
                      Scenario {currentConjointScenarioIndex + 1} of {currentQuestion.conjoint_scenarios || Math.min(12, Math.max(8, attributes.length * 2))}
                    </div>
                  </div>

                  {/* Profile options */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {taskProfiles.map((profile, index) => {
                      const isSelected = currentResponse === profile.id;

                      return (
                        <div
                          key={profile.id}
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-gray-300 bg-white hover:border-blue-300 hover:shadow-sm'
                          }`}
                          onClick={() => handleResponseChange(profile.id)}
                        >
                          <div className="text-center mb-4">
                            <h4 className="font-semibold text-gray-900 text-lg">
                              Option {String.fromCharCode(65 + index)}
                            </h4>
                          </div>

                          <div className="space-y-3">
                            {attributes.map(attr => (
                              <div key={attr.name} className="border-b border-gray-100 pb-2 last:border-b-0">
                                <div className="flex flex-col space-y-1">
                                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    {attr.name.replace(/_/g, ' ')}
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {String(profile.attributes[attr.name]).replace(/_/g, ' ')}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>

                          {isSelected && (
                            <div className="mt-4 flex justify-center">
                              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                <Check className="w-5 h-5 text-white" />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        );

      case 'image_hotspot':
        // Build public image URL from question config
        const hotspotImageUrl = currentQuestion.image_url 
          ? (currentQuestion.image_url.startsWith('http') 
              ? currentQuestion.image_url 
              : `${import.meta.env.VITE_API_URL || ''}/api/question-images/public/${survey.id}/${currentQuestion.image_url.split('/').pop()}`)
          : '';
        
        return (
          <div className="space-y-4">
            <ImageHotspotResponse
              imageUrl={hotspotImageUrl}
              boundingBoxes={currentQuestion.bounding_boxes || []}
              rankingCount={currentQuestion.ranking_count || 3}
              onComplete={(clicks) => {
                // Format response data for submission
                const responseData = { clicks };
                handleResponseChange(responseData);
              }}
              disabled={isSubmitting}
            />
          </div>
        );

      case 'info_screen':
        // Information display - no response needed, user just clicks continue
        // Use key to force remount when question changes, ensuring clean loading states
        return (
          <InfoScreenDisplay
            key={`info-screen-${currentQuestion.id || currentQuestionIndex}`}
            content={currentQuestion.text}
            description={currentQuestion.description}
            displayTimeSeconds={currentQuestion.display_time_seconds}
            mediaUrls={orderedQuestionMediaUrls}
            onContinue={() => {
              // Auto-submit with "viewed" response and move to next question
              handleResponseChange('viewed');
              // Trigger submit after setting response
              setTimeout(() => handleSubmitResponse(), 100);
            }}
            disabled={isSubmitting}
          />
        );

      case 'percentage_allocation':
        const allocOptions = currentQuestion.allocation_options || currentQuestion.choices || [];
        const allocTarget = currentQuestion.allocation_target || 100;
        const allocResponse = (currentResponse as Record<string, number>) || {};
        const autoFillLast = currentQuestion.auto_fill_last !== false; // Default true
        const allowZero = currentQuestion.allow_zero !== false; // Default true

        // Calculate current total
        const allocTotal = Object.values(allocResponse).reduce((sum, val) => sum + (val || 0), 0);

        // Get status color
        const getAllocStatusColor = () => {
          if (allocTotal > allocTarget) return 'text-red-600';
          if (allocTotal < allocTarget) return 'text-amber-600';
          return 'text-emerald-600';
        };

        const getAllocStatusBg = () => {
          if (allocTotal > allocTarget) return 'bg-red-50 border-red-200';
          if (allocTotal < allocTarget) return 'bg-amber-50 border-amber-200';
          return 'bg-emerald-50 border-emerald-200';
        };

        return (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              Allocate values across the options below. {autoFillLast ? 'The last option will be auto-calculated.' : `Total must equal ${allocTarget}.`}
            </div>

            <div className="space-y-3">
              {allocOptions.map((option: string, index: number) => {
                const isLastOption = autoFillLast && index === allocOptions.length - 1;
                const otherOptionsTotal = allocOptions
                  .slice(0, -1)
                  .reduce((sum: number, opt: string) => sum + (allocResponse[opt] || 0), 0);
                const autoValue = isLastOption ? Math.max(0, allocTarget - otherOptionsTotal) : null;
                const currentValue = isLastOption && autoFillLast ? autoValue : (allocResponse[option] || 0);

                return (
                  <div key={index} className="flex items-center justify-between gap-4 p-3 bg-white border border-gray-200 rounded-lg">
                    <span className="flex-1 text-gray-900 font-medium">{option}</span>
                    <div className="flex items-center gap-2">
                      {isLastOption ? (
                        <div className="flex items-center gap-2">
                          <span className={`w-20 text-right font-semibold tabular-nums ${autoValue !== null && autoValue < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                            {autoValue !== null ? autoValue : 0}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">Auto</span>
                        </div>
                      ) : (
                        <>
                          <input
                            type="number"
                            min={allowZero ? 0 : 1}
                            max={allocTarget}
                            step={1}
                            value={currentValue || ''}
                            onChange={(e) => {
                              let val = parseInt(e.target.value) || 0;
                              // Enforce minimum of 1 when allow_zero is false
                              if (!allowZero && val < 1 && e.target.value !== '') {
                                val = 1;
                              }
                              // Initialize all options with 0 first, then spread existing values
                              const initializedResponse = allocOptions.reduce((acc: Record<string, number>, opt: string) => {
                                acc[opt] = allocResponse[opt] || 0;
                                return acc;
                              }, {} as Record<string, number>);
                              const newResponse = { ...initializedResponse, [option]: val };

                              // If auto_fill_last, calculate the last option's value
                              if (autoFillLast && allocOptions.length > 0) {
                                const lastOption = allocOptions[allocOptions.length - 1];
                                const otherTotal = allocOptions
                                  .slice(0, -1)
                                  .reduce((sum: number, opt: string) => {
                                    if (opt === option) return sum + val;
                                    return sum + (initializedResponse[opt] || 0);
                                  }, 0);
                                newResponse[lastOption] = Math.max(0, allocTarget - otherTotal);
                              }

                              handleResponseChange(newResponse);
                            }}
                            className="w-20 px-3 py-2 text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent tabular-nums"
                            placeholder={allowZero ? "0" : "1"}
                          />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total indicator */}
            <div className={`flex items-center justify-between p-3 rounded-lg border ${getAllocStatusBg()}`}>
              <span className="font-medium text-gray-700">Total</span>
              <div className="flex items-center gap-2">
                <span className={`font-bold text-lg tabular-nums ${getAllocStatusColor()}`}>
                  {autoFillLast
                    ? allocOptions.slice(0, -1).reduce((sum: number, opt: string) => sum + (allocResponse[opt] || 0), 0) +
                      Math.max(0, allocTarget - allocOptions.slice(0, -1).reduce((sum: number, opt: string) => sum + (allocResponse[opt] || 0), 0))
                    : allocTotal
                  }
                </span>
                <span className="text-gray-500">/ {allocTarget}</span>
                {allocTotal === allocTarget && (
                  <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </div>

            {/* Warning messages */}
            {allocTotal > allocTarget && (
              <p className="text-sm text-red-600">
                Total exceeds {allocTarget}. Please reduce some values.
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        @keyframes gentleBounce {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        @keyframes waveform {
          0%, 100% { 
            transform: scaleY(0.3);
            opacity: 0.6;
          }
          50% { 
            transform: scaleY(1);
            opacity: 1;
          }
        }
      `}</style>
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm">
          {/* Preview Mode Banner */}
          {isPreviewMode && (
            <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-center">
              <span className="text-xs font-medium text-amber-700">Preview Mode — responses are not saved</span>
            </div>
          )}

          {/* Progress Header */}
          <div className="px-6 pt-6 pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-gray-500">
                Question {currentQuestionIndex + 1} of {survey.questions.length}
              </div>
              <div className="text-sm text-gray-500">
                {Math.round(((currentQuestionIndex + 1) / survey.questions.length) * 100)}% Complete
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestionIndex + 1) / survey.questions.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Question Content */}
          <div className="px-6 py-8">
            {!isAnalyzing ? (
              <>
                <div className="mb-8">

                  {/* Hide question text header for info_screen since InfoScreenDisplay shows it */}
                  {currentQuestion.type !== 'info_screen' && (
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">
                      {followupQuestion || currentQuestion.text}
                    </h2>
                  )}
                  
                  {renderQuestionInput()}
                </div>

                {/* Navigation Buttons - hidden for info_screen since it has its own Continue button */}
                {currentQuestion.type !== 'info_screen' && (
                  <div className="flex justify-between">
                    {isPreviewMode ? (
                      <button
                        type="button"
                        onClick={handlePreviewSkip}
                        className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        Skip
                      </button>
                    ) : <div />}
                    <Button
                      variant="primary"
                      onClick={() => handleSubmitResponse()}
                      disabled={!canProceed || isSubmitting || isAnalyzing}
                      icon={followupQuestion ? <ArrowRight className="w-4 h-4" /> : isLastQuestion ? <Check className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                    >
                      {isSubmitting
                        ? UI_TEXT.AI_SURVEY.SESSION.BUTTONS.SUBMITTING 
                        : followupQuestion
                          ? UI_TEXT.BUTTONS.CONTINUE
                          : UI_TEXT.AI_SURVEY.SESSION.BUTTONS.NEXT
                      }
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="w-24 h-24 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
                  <p className="text-lg font-medium text-gray-900">{analyzingMessage}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default SurveySession;